/* Loomish Atmosphere - the threshold.
 *
 * Bronze. The mark, centred, small. It holds while the site loads behind it,
 * then leads: the photographs rise around it into the wheel, the ground lifts,
 * and the wheel is left turning over the live site. The visitor's own push
 * opens the ring into a rail, sends it down to the slider, and sends the mark
 * up into the header, where it stays as the brand.
 *
 * It is one element the whole way up. The mark you see in the middle of the
 * bronze flies, as itself, onto the header's own mark and hands over in a
 * single frame - the header owns the brand from then on, on every visit.
 *
 * Wheel timeline: Codrops "Entrance Animation for Images" variation 1
 * (github.com/d3adrabbit/EntranceAnimationForImages), with the circle closed
 * concentric with the mark - and all of it played by the compositor, not
 * drawn by script (2026-10-21; see THE WHEEL IS PLAYED BY THE COMPOSITOR).
 *
 * The header and the hero are NOT edited by this file. It reads their DOM,
 * borrows a position, and toggles a class. Nothing of theirs is rewritten.
 *
 * ---------------------------------------------------------------------------
 * THE PAGE DOES NOT MOVE UNTIL THE PHOTOGRAPHS HAVE ARRIVED
 * ---------------------------------------------------------------------------
 * The page used to go live at the reveal, while the wheel was still turning.
 * A visitor who scrolls hard - and most do - was at the slider inside half a
 * second, where the slider's own scroll reveal met them with a 520ms rise
 * while the rail was still descending theatrically upstairs. Two arrivals for
 * one object. The hold on the slider could not prevent it, because the visitor
 * got down there under their own power.
 *
 * So the page is held from the first frame until the rail lands. Not by
 * refusing the gesture - by spending it. See Gate.
 */
(function () {
  'use strict';

  var TWO_PI = Math.PI * 2;
  var live = [];

  /* Whatever the session was using before the threshold took it over. Putting
     back a hardcoded 'auto' would quietly change the behaviour of any site
     that had deliberately set 'manual'. */
  var priorRestore = null;

  /** Angles, folded into a single turn. 540 and 180 render the same pixel. */
  function wrap(deg) { return ((deg % 360) + 360) % 360; }
  /** From a wrapped angle, the nearest whole turn - never more than half away */
  function nearestTurn(deg) { return deg > 180 ? 360 : 0; }

  function stamp() {
    try {
      if (window.performance && window.performance.now) return window.performance.now();
    } catch (e) {}
    return Date.now();
  }

  /**
   * A photograph's name without its size or format - the same key the phone
   * gallery uses (assets/loomish-rail.js), written out here because this
   * file can run before that one has loaded.
   */
  function keyOf(src) {
    if (window.LoomishRail && window.LoomishRail.keyOf) return window.LoomishRail.keyOf(src);
    if (!src) return '';
    var name = String(src).split('?')[0].split('/').pop() || '';
    name = name.replace(/\.(png|jpe?g|webp|gif|avif)$/i, '');
    return name.replace(/_(\d+x\d*|x\d+)$/i, '').toLowerCase();
  }

  /** Tell the page something happened, on any engine. */
  function say(name) {
    try {
      document.dispatchEvent(new CustomEvent(name));
    } catch (e) {
      var ev = document.createEvent('Event');
      ev.initEvent(name, true, true);
      document.dispatchEvent(ev);
    }
  }

  /*
   * ScrollTrigger re-measures every trigger on the page when `load` fires -
   * 92ms on a mid phone in a trace, landing in the middle of the opening. A
   * page held shut has nothing worth measuring (release() measures it the
   * moment it is let go), so while the threshold holds, `load` is taken off
   * ScrollTrigger's list, and it is put back with the page.
   */
  var ST_EVENTS = 'visibilitychange,DOMContentLoaded,load,resize';
  function holdTriggers(on) {
    var ST = window.ScrollTrigger;
    if (!ST || !ST.config) return;
    try { ST.config({ autoRefreshEvents: on ? 'visibilitychange,DOMContentLoaded,resize' : ST_EVENTS }); } catch (e) {}
  }

  /*
   * AHEAD OF THE PAGE (2026-10-25).
   *
   * This file starts the moment it arrives now (see boot), which on a slow
   * phone is long before the page below it has been read. So whatever it
   * needs from further down - the room, the gallery - may not exist yet, or
   * may exist half-read. These two say when it is whole.
   */

  /**
   * Whether the parser has finished with an element, so everything inside it
   * is there. Its next sibling only exists once its end tag has been read.
   * Looked for no further than its own section: a script appending to the
   * body could otherwise pass for the page having moved on.
   */
  function closed(el) {
    if (document.readyState !== 'loading') return true;
    for (var n = el; n && n.nodeType === 1; n = n.parentNode) {
      if (n.nextSibling) return true;
      if (n.classList && n.classList.contains('shopify-section')) return false;
    }
    return false;
  }

  /** Once the whole page has been read - at once if it has. */
  function whenParsed(fn) {
    if (document.readyState !== 'loading') { fn(); return; }
    var on = function () {
      if (document.readyState === 'loading') return;
      document.removeEventListener('readystatechange', on);
      fn();
    };
    document.addEventListener('readystatechange', on);
  }

  /* ------------------------------------------------------ the visible clock */

  /**
   * TIME THAT ONLY PASSES WHILE SOMEBODY IS LOOKING.
   *
   * This is the anti-glitch mechanism, and it is one idea: every act of the
   * entrance is scheduled on the same clock that drives the animation.
   *
   * It used to have two. GSAP runs on requestAnimationFrame, which a browser
   * STOPS when the tab is hidden. setTimeout does not stop — it is throttled
   * to about a second in a background tab, and on a phone it is frozen and
   * then released in a burst when the app comes forward. So the moment a
   * visitor switched away the two drifted apart: the timers ran the entrance
   * to its end while the wheel stood exactly where it froze. Come back and
   * the ground has lifted off a half-built wheel, or every remaining ceiling
   * fires inside one frame and the whole thing snaps.
   *
   * That is the entire class of "it worked the first time and glitched the
   * next", and the fix is not to patch each symptom. It is to delete the
   * second clock. Nothing in this file is scheduled in wall time.
   *
   * A tab nobody is looking at therefore holds the entrance exactly where it
   * is — which is also the right answer, and fixes something else on its own:
   * opening the site in a background tab used to burn the whole opening, so
   * arriving at that tab got you a static page. Now the opening is there when
   * the visitor is, which is the only time it was ever for.
   */
  function Clock() {
    var self = this;
    this.jobs = [];
    this.t = 0;
    this.seq = 1;
    this.last = 0;
    this.armed = false;

    this.step = function () {
      self.armed = false;
      var now = stamp();
      /* `document.hidden` is belt and braces: rAF normally stops outright,
         but some engines throttle it to a frame a second instead. */
      var seen = !document.hidden;

      if (seen && self.last) {
        var dt = now - self.last;
        /*
         * A gap of seconds is a freeze - a phone that put the app away without
         * saying so - not a slow frame, and counts as one ordinary frame.
         *
         * It used to be GSAP's own half second (lagSmoothing 500/33), so that
         * this clock and GSAP's tweens stepped together. But the wheel is
         * played by the compositor now, in real time, and the threshold now
         * runs while the page is still arriving behind it: a mid phone has
         * single tasks of 600ms and more in that window (the store's own
         * scripts, the page being laid out). Counted as 33ms, each one left
         * every act that much behind the wheel it follows (2026-10-25).
         */
        if (dt > 2500) dt = 33;
        if (dt > 0) { self.t += dt; self.fire(); }
      }

      self.last = seen ? now : 0;
      if (self.jobs.length) self.arm();
    };
  }

  Clock.prototype.arm = function () {
    if (this.armed) return;
    this.armed = true;
    var s = this.step;
    if (window.requestAnimationFrame) window.requestAnimationFrame(function () { s(); });
    else window.setTimeout(function () { s(); }, 16);
  };

  Clock.prototype.at = function (ms, fn) {
    var job = { id: this.seq++, at: this.t + (ms > 0 ? ms : 0), fn: fn };
    this.jobs.push(job);
    this.arm();
    return job.id;
  };

  Clock.prototype.clear = function (id) {
    for (var i = this.jobs.length - 1; i >= 0; i--) {
      if (this.jobs[i].id === id) { this.jobs.splice(i, 1); return; }
    }
  };

  Clock.prototype.fire = function () {
    var due = [], keep = [], i;
    for (i = 0; i < this.jobs.length; i++) {
      (this.jobs[i].at <= this.t ? due : keep).push(this.jobs[i]);
    }
    if (!due.length) return;
    this.jobs = keep;
    due.sort(function (a, b) { return a.at - b.at; });
    for (i = 0; i < due.length; i++) {
      /* One job that throws must not strand the others — several of them are
         the ceilings that keep this from leaving the page shut. */
      try { due[i].fn(); } catch (e) {
        if (window.console) window.console.error('[loomish-atmosphere]', e);
      }
    }
  };

  var clock = new Clock();

  /* --------------------------------------------------------- the compositor */

  /*
   * THE WHEEL IS PLAYED BY THE COMPOSITOR (2026-10-21).
   *
   * Every move the wheel made used to be a GSAP tween, drawn a frame at a time
   * by the page's own script thread - the busiest thread in the building
   * during an opening. The room's photograph decoding, the hero and the header
   * answering the reveal, the store's own scripts arriving: a trace on a mid
   * phone found single tasks of 125-333ms landing inside the turn, and each
   * one was a frozen frame of the ring.
   *
   * So the wheel now writes every move out in full before it makes it - each
   * element, each frame - and hands the result to the compositor (Web
   * Animations), which plays transform and opacity on a thread of its own. A
   * busy page cannot stop the ring now. It cannot even see it.
   *
   * The moves are still composed the way a timeline is. A Part is one element:
   * a resting pose, and moves laid over it, each a set of targets with a
   * start, a length and one of GSAP's own curves (written out below, so a
   * baked frame is the frame GSAP would have drawn). Moves ADD - each carries
   * the distance from the target before it - so two beats may overlap and
   * blend into one curve instead of stopping in between.
   */
  var CAN = !!(window.Element && Element.prototype.animate);
  var RAD = Math.PI / 180;

  /* Where in STRAIGHTEN the rail begins to open - shared with the mark's flight,
     which lifts at that same instant. */
  var FORM_AT = 0.7;

  var EASE = {
    none: function (p) { return p; },
    'power1.out': function (p) { return 1 - (1 - p) * (1 - p); },
    'power2.in': function (p) { return p * p * p; },
    'power2.out': function (p) { var q = 1 - p; return 1 - q * q * q; },
    'power2.inOut': function (p) { return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(2 - 2 * p, 3) / 2; },
    'power3.inOut': function (p) { return p < 0.5 ? 8 * p * p * p * p : 1 - Math.pow(2 - 2 * p, 4) / 2; }
  };

  /* The quad ease-out, as CSS, for the few things handed over as plain keyframes. */
  var OUT1 = 'cubic-bezier(0.5, 1, 0.89, 1)';

  /**
   * A CSS cubic-bezier, also as numbers. `at` is the curve the compositor
   * plays; `when` is the same curve read backwards - the moment it reaches a
   * given distance. The ripple needs the second: it is timed to the instant
   * each card reaches the top of a ring that is still gathering speed.
   */
  function Bezier(x1, y1, x2, y2) {
    var cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    var cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    var X = function (u) { return ((ax * u + bx) * u + cx) * u; };
    var Y = function (u) { return ((ay * u + by) * u + cy) * u; };
    var solve = function (f, v) {
      var lo = 0, hi = 1, u = v;
      for (var i = 0; i < 32; i++) { u = (lo + hi) / 2; if (f(u) < v) lo = u; else hi = u; }
      return u;
    };
    this.css = 'cubic-bezier(' + x1 + ', ' + y1 + ', ' + x2 + ', ' + y2 + ')';
    this.at = function (x) { return x <= 0 ? 0 : x >= 1 ? 1 : Y(solve(X, x)); };
    this.when = function (y) { return y <= 0 ? 0 : y >= 1 ? 1 : X(solve(Y, y)); };
  }

  /* The two circles: GSAP's power2.inOut, the curve they were approved on. */
  var TURN = new Bezier(0.65, 0, 0.35, 1);
  /* A photograph turning over: quick through the middle, soft at both ends. */
  var FLIP = new Bezier(0.7, 0, 0.3, 1);
  /* The ring's breath, rising and falling with the speed of the turn. */
  var BELL = new Bezier(0.37, 0, 0.63, 1);

  /**
   * One element, its resting pose, and the moves laid over it.
   * `fmt` turns a pose into a keyframe.
   */
  function Part(el, pose, fmt) {
    this.el = el;
    this.pose = pose;
    this.fmt = fmt;
    this.moves = [];
    this.last = {};
    for (var k in pose) this.last[k] = pose[k];
  }

  Part.prototype.to = function (target, at, dur, ease) {
    var d = {}, any = false;
    for (var k in target) {
      if (!(k in this.last)) { this.pose[k] = 0; this.last[k] = 0; }
      d[k] = target[k] - this.last[k];
      this.last[k] = target[k];
      if (d[k]) any = true;
    }
    if (any) this.moves.push({ d: d, at: at, dur: dur > 0 ? dur : 0, ease: EASE[ease] || EASE.none });
    return this;
  };

  /** The same, as a distance from wherever the last move left it. */
  Part.prototype.by = function (delta, at, dur, ease) {
    var t = {};
    for (var k in delta) t[k] = (k in this.last ? this.last[k] : 0) + delta[k];
    return this.to(t, at, dur, ease);
  };

  Part.prototype.at = function (t) {
    var v = {}, k, i, m, p;
    for (k in this.pose) v[k] = this.pose[k];
    for (i = 0; i < this.moves.length; i++) {
      m = this.moves[i];
      if (t <= m.at) continue;
      p = m.dur ? (t - m.at) / m.dur : 1;
      p = p >= 1 ? 1 : m.ease(p);
      for (k in m.d) v[k] += m.d[k] * p;
    }
    return v;
  };

  Part.prototype.end = function () {
    var e = 0;
    for (var i = 0; i < this.moves.length; i++) e = Math.max(e, this.moves[i].at + this.moves[i].dur);
    return e;
  };

  /**
   * A pose turning about a point `py` below the card's centre, rewritten as
   * the same picture turning about the centre itself.
   *
   * The build turns each card on an offset pivot (Codrops sets the origin r1
   * below the card), and changes that pivot halfway through - which on a
   * fixed origin would be a jump. Written out frame by frame, the offset is
   * folded into the translation instead: every frame shares one origin, and
   * the pivot change is only a different set of numbers.
   */
  function canon(v) {
    var py = v.py || 0;
    if (!py) return v;
    var r = v.r * RAD, ry = v.ry * RAD, rx = v.rx * RAD;
    var sy = v.s * py;
    var y0 = sy * Math.cos(rx), z0 = sy * Math.sin(rx);
    var x1 = Math.sin(ry) * z0, z1 = Math.cos(ry) * z0;
    var x2 = Math.cos(r) * x1 - Math.sin(r) * y0;
    var y2 = Math.sin(r) * x1 + Math.cos(r) * y0;
    return { x: v.x - x2, y: v.y + py - y2, z: v.z - z1, r: v.r, ry: v.ry, rx: v.rx, s: v.s };
  }

  /*
   * Keyframes. Each element is always written with the same list of
   * functions, so the compositor interpolates them number for number - never
   * through a matrix, which would take every turn the short way round.
   */
  function cardFrame(v) {
    return {
      transform: 'translate3d(' + v.x.toFixed(2) + 'px,' + v.y.toFixed(2) + 'px,' + v.z.toFixed(2) + 'px) rotate(' +
        v.r.toFixed(2) + 'deg) rotateY(' + v.ry.toFixed(2) + 'deg) rotateX(' + v.rx.toFixed(2) + 'deg) scale(' + v.s.toFixed(4) + ')'
    };
  }
  function formFrame(v) { return cardFrame(canon(v)); }
  function cardFrameO(v) { var f = cardFrame(v); f.opacity = v.o.toFixed(3); return f; }
  function groupFrame(v) {
    return {
      transform: 'translate3d(' + v.x.toFixed(2) + 'px,' + v.y.toFixed(2) + 'px,0px) rotate(' + v.r.toFixed(2) +
        'deg) rotateX(' + v.rx.toFixed(2) + 'deg) scale(' + v.sx.toFixed(4) + ',' + v.sy.toFixed(4) + ')'
    };
  }
  function spinFrame(v) { return { transform: 'rotate(' + v.a.toFixed(2) + 'deg)' }; }
  function breathFrame(v) {
    return { transform: 'rotateX(' + v.lx.toFixed(2) + 'deg) rotateY(' + v.ly.toFixed(2) + 'deg) scale(' + v.s.toFixed(4) + ')' };
  }
  function faceFrame(v) { return { transform: 'scale(1,' + v.k.toFixed(4) + ')', opacity: v.o.toFixed(3) }; }
  function imgFrame(v) {
    return { transform: 'translate3d(' + v.tx.toFixed(2) + 'px,0px,0px) scale(' + v.m.toFixed(4) + ',' + (v.m / v.k).toFixed(4) + ')' };
  }

  /** Paused while nobody is looking, and only what this paused is resumed. */
  function holdAnim(a, hidden) {
    try {
      if (hidden) {
        if (a.playState === 'running') { a.pause(); a.__lmHeld = true; }
      } else if (a.__lmHeld) {
        a.__lmHeld = false;
        a.play();
      }
    } catch (e) {}
  }

  /* ------------------------------------------------------------- the wheel */

  function Wheel(root, opts) {
    this.root = root;
    this.scene = root.querySelector('.lmat__scene');
    this.group = root.querySelector('[data-lmat-group]');
    this.breath = root.querySelector('[data-lmat-breath]');
    this.spin = root.querySelector('[data-lmat-spin]');
    var holder = this.spin || this.group;
    this.cards = holder ? Array.prototype.filter.call(holder.children, function (c) {
      return c.classList.contains('lmat__card');
    }) : [];
    this.flips = this.cards.map(function (c) { return c.querySelector('.lmat__flip'); });
    this.faces = this.cards.map(function (c) { return Array.prototype.slice.call(c.querySelectorAll('.lmat__face')); });
    this.opts = opts;
    this.anims = [];
    this.rate = 1;
  }

  Wheel.prototype.keep = function (a) { this.anims.push(a); return a; };

  /** One Part to the compositor: `fps` frames a second, straight lines between. */
  Wheel.prototype.play = function (part, total, fps, delay) {
    var n = Math.max(2, Math.round(total * fps) + 1);
    var frames = new Array(n);
    for (var j = 0; j < n; j++) {
      var f = part.fmt(part.at(total * j / (n - 1)));
      f.offset = j / (n - 1);
      frames[j] = f;
    }
    return this.keep(part.el.animate(frames, { duration: total * 1000, delay: (delay || 0) * 1000, fill: 'both' }));
  };

  /** A moment on the same timeline as the motion, for what must happen at it. */
  Wheel.prototype.timer = function (sec, fn) {
    var a = this.keep(this.root.animate([], { duration: Math.max(1, sec * 1000) }));
    a.onfinish = function () { fn(); };
    return a;
  };

  /**
   * The build, as Codrops wrote it (variation 1), with one correction: the
   * circle now closes AROUND THE MARK.
   *
   * Codrops' last move leaves the circle's centre r1 below the point the
   * wheel turns on. The ring sat low of the mark, and once it turned it
   * rolled around it - its centre swinging through a circle of its own, 40px
   * across on a phone, so every card ran a different path. The last move's
   * height is taken up by r1, and the angles are written from the top, so
   * the circle closes concentric with the mark for any number of cards, each
   * card pointing straight out from the centre, and turns true.
   */
  Wheel.prototype.formation = function () {
    var o = this.opts, cards = this.cards, n = cards.length;
    var sp = o.speed || 1;
    var cardH = cards[0].offsetHeight || 120;
    var r1 = o.radius1, r2 = o.radius2, st = o.stagger;
    var y0 = window.innerHeight / 2 + cardH * 1.5;
    var T1 = 0.5 + st * (n - 1);
    var parts = [];

    for (var i = 0; i < n; i++) {
      var turn = (i + 1) * 360 / n;
      var a = turn * RAD - Math.PI / 2;
      var p = new Part(cards[i], { x: 0, y: y0, z: 0, r: 0, ry: 0, rx: -180, s: 3, py: 0 }, formFrame);
      p.to({ y: 0, rx: 0, s: 1 }, i * st / sp, 0.5 / sp, 'power1.out');
      /* The pivot drops to the wheel's centre - unseen: every card is at rest. */
      p.to({ py: r1 }, T1 / sp, 0, 'none');
      p.to({ y: -r1 }, T1 / sp, 0.5 / sp, 'power1.out');
      p.to({ r: i * 360 / n, ry: 15 }, T1 / sp, 1 / sp, 'power1.out');
      p.to({ x: r2 * Math.cos(a), y: r2 * Math.sin(a) - r1, r: turn }, (T1 + 1) / sp, 1 / sp, 'power1.out');
      p.to({ ry: 180 }, (T1 + 1) / sp, 1 / sp, 'power1.out');
      parts.push(p);
    }

    var total = (T1 + 2) / sp;
    var half = 0.5 / sp, s1 = (T1 + 1) / sp, stS = st / sp;

    /*
     * THE PRESENCE IS THE PHOTOGRAPHS', NOT THE CARDS'.
     *
     * Codrops fades the cards themselves (0.8 in, 0.9 on the ring). But a 3D
     * element that is less than opaque is FLATTENED - its faces drawn into its
     * own plane - so in Chrome every card on the turned ring showed its front
     * photograph mirrored, never the back one chosen for it, and the posters
     * read backwards. The same fade is given to the faces instead, which have
     * nothing inside them to flatten.
     */
    var presence = function (i, t) {
      var a0 = i * stS, e0 = a0 + half;
      if (t <= a0) return 0.8;
      if (t < e0) return 0.8 + 0.2 * EASE['power1.out']((t - a0) / half);
      if (t <= s1) return 1;
      if (t < total) return 1 - 0.1 * EASE['power1.out']((t - s1) / (total - s1));
      return 0.9;
    };

    return { parts: parts, total: total, half: half, s1: s1, st: stS, presence: presence };
  };

  /**
   * Build the wheel, and book its turn on the same timeline.
   *
   * `onFormed` fires when the circle has closed; `onTurned` when the circles
   * are done. The circles are not started from that first callback, as they
   * were: they are given to the compositor up front, to begin the frame the
   * build ends - so no task on the page, however long, can put a pause
   * between the circle closing and the circle turning.
   */
  Wheel.prototype.run = function (onFormed, onTurned) {
    if (!CAN || !this.cards.length || !this.spin || !this.group) {
      if (onFormed) onFormed();
      if (onTurned) onTurned();
      return;
    }

    var f = this.form = this.formation();
    this.group.style.opacity = '1';

    var first = null;
    for (var i = 0; i < f.parts.length; i++) {
      var a = this.play(f.parts[i], f.total, 60, 0);
      if (!first) first = a;
      var kf = [
        { opacity: 0.8, offset: 0 },
        { opacity: 0.8, offset: i * f.st / f.total, easing: OUT1 },
        { opacity: 1, offset: Math.min(1, (i * f.st + f.half) / f.total) },
        { opacity: 1, offset: f.s1 / f.total, easing: OUT1 },
        { opacity: 0.9, offset: 1 }
      ];
      for (var k = 0; k < this.faces[i].length; k++) {
        this.keep(this.faces[i][k].animate(kf, { duration: f.total * 1000, fill: 'both' }));
      }
    }

    this.formAnim = first;
    this.started = true;

    if (this.opts.circles > 0) this.circles(f.total, onTurned);
    else this.idle(f.total);

    first.onfinish = function () { if (onFormed) onFormed(); };
  };

  /** How long the build takes at the current pace, before it is run. */
  Wheel.prototype.formTime = function () {
    if (!this.cards.length) return 0;
    /* 0.5s fly-in plus its stagger tail, 1s to fan, 1s to open the circle */
    var t = 0.5 + this.opts.stagger * (this.cards.length - 1) + 1 + 1;
    return t / (this.opts.speed || 1);
  };

  /**
   * TWO CIRCLES, THEN IN (2026-10-15) - and how they are drawn (2026-10-21).
   *
   * The founder's direction stands: the cards show up, turn two circles, and
   * go in, with nobody having to touch or scroll. Three things are added to
   * the turn, all on the compositor and all on the turn's own clock:
   *
   *   THE BREATH  As the ring gathers speed it swells a few percent and
   *               leans back a few degrees, and both settle as it slows -
   *               a camera rising slightly over a thing in motion. It reads
   *               as weight: the ring is an object, not a graphic.
   *
   *   THE RIPPLE  The ring is built showing its second set of photographs.
   *               As each card passes the top of the circle it turns over,
   *               one after another around the ring, and shows the piece the
   *               gallery is made of - developed as it passes through the
   *               light. The turn is eased, so the ripple is too: it begins
   *               unhurried and runs faster as the ring does.
   *
   *   THE WHOLE TURNS  The circle ends where it began, so the outro has
   *               nothing to unwind.
   *
   * It is the SPIN wrapper that turns, not the cards, and the BREATH wrapper
   * that leans, so neither touches what the build left on each card.
   */
  Wheel.prototype.circles = function (delay, done) {
    var o = this.opts, n = this.cards.length;
    var turns = o.circles, dur = o.circlesTime;
    var total = 360 * turns;
    var ms = dur * 1000, dl = delay * 1000;
    var anims = [];

    var spin = this.spin.animate(
      [{ transform: 'rotate(0deg)' }, { transform: 'rotate(' + total + 'deg)' }],
      { duration: ms, delay: dl, easing: TURN.css, fill: 'both' }
    );
    anims.push(spin);

    /* The lean is mostly back (X), a little round (Y): not a tilting plate
       but a camera rising and drifting a step to one side as the ring
       gathers speed, and settling back as it slows. */
    var B = o.breath || 0, L = o.lean || 0, LY = -L * 0.35;
    if (this.breath && (B || L)) {
      var rest = 'rotateX(0deg) rotateY(0deg) scale(1)';
      anims.push(this.breath.animate([
        { transform: rest, easing: BELL.css },
        { transform: 'rotateX(' + L + 'deg) rotateY(' + LY.toFixed(2) + 'deg) scale(' + (1 + B) + ')', offset: 0.5, easing: BELL.css },
        { transform: rest }
      ], { duration: ms, delay: dl, fill: 'both' }));
    }

    /*
     * The ripple's gate is half a step past the top, so no card is standing
     * on it when a circle begins. The fronts are turned on the LAST circle:
     * the first goes round at speed showing the backs, and as the ring slows
     * each card turns over in order as it passes the top - slower, so each
     * turn can be read, and the last of them just before the ring comes to
     * rest. Each card's turn is centred on the moment it reaches the gate -
     * found by reading the turn's curve backwards. (It also leaves the
     * gallery's photographs a full second longer to arrive before the
     * landing cards change to them, unseen; see armSwaps.)
     */
    var sched = [];
    var tau = Math.min(0.62, dur * 0.28);
    if (o.ripple) {
      var gate = 180 / n;
      for (var i = 0; i < n; i++) {
        var pass = ((gate - (i + 1) * 360 / n) % 360 + 360) % 360;
        if (pass < 0.5) pass += 360;
        pass += 360 * Math.max(0, Math.ceil(turns) - 1);
        if (pass > total || !this.flips[i]) { sched.push(null); continue; }
        var tc = TURN.when(pass / total) * dur;
        var s0 = Math.max(0, Math.min(dur - tau, tc - tau / 2));
        sched.push({ s: s0, e: s0 + tau });
        var over = 'rotateY(180deg)';
        anims.push(this.flips[i].animate([
          { transform: 'rotateY(0deg)', offset: 0 },
          { transform: 'rotateY(0deg)', offset: s0 / dur, easing: FLIP.css },
          { transform: over, offset: (s0 + tau) / dur },
          { transform: over, offset: 1 }
        ], { duration: ms, delay: dl, fill: 'both' }));
      }
    }

    for (var j = 0; j < anims.length; j++) this.keep(anims[j]);
    this.sched = sched;
    this.tau = tau;

    this.circ = {
      clock: spin, delay: delay, dur: dur, anims: anims,
      spinAt: function (t) { return total * TURN.at(t / dur); },
      breathAt: function (t) {
        var x = t / dur;
        var b = x < 0.5 ? BELL.at(2 * x) : BELL.at(2 - 2 * x);
        return { s: 1 + B * b, lx: L * b, ly: LY * b };
      },
      flipAt: function (i, t) {
        var f = sched[i];
        if (!f || t <= f.s) return 0;
        if (t >= f.e) return 180;
        return 180 * FLIP.at((t - f.s) / tau);
      }
    };

    spin.onfinish = function () { if (done) done(); };
  };

  /**
   * The wheel at rest, turning - for a threshold set to wait for its push
   * rather than open itself. An endless slow spin, and each card turning
   * over now and then and back, never two in step.
   */
  Wheel.prototype.idle = function (delay) {
    var o = this.opts, dl = delay * 1000, anims = [];
    var per = o.spin > 0 ? o.spin : 0;
    var P = 7.2;
    var spin = null, phases = [];

    if (per) {
      spin = this.spin.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
        { duration: per * 1000, delay: dl, iterations: Infinity, easing: 'linear', fill: 'both' });
      anims.push(spin);
    }
    if (o.flip) {
      for (var i = 0; i < this.flips.length; i++) {
        if (!this.flips[i]) { phases.push(-1); continue; }
        var ph = Math.random() * P;
        phases.push(ph);
        anims.push(this.flips[i].animate([
          { transform: 'rotateY(0deg)', offset: 0 },
          { transform: 'rotateY(0deg)', offset: 2.4 / P, easing: FLIP.css },
          { transform: 'rotateY(180deg)', offset: 3.6 / P },
          { transform: 'rotateY(180deg)', offset: 6.0 / P, easing: FLIP.css },
          { transform: 'rotateY(0deg)', offset: 1 }
        ], { duration: P * 1000, delay: dl + ph * 1000, iterations: Infinity, fill: 'both' }));
      }
    }
    for (var j = 0; j < anims.length; j++) this.keep(anims[j]);
    if (!anims.length) return;

    /* Every animation made in one task shares one start, whatever its own
       delay - so any of them is a clock for all of them. */
    this.circ = {
      clock: anims[0], delay: delay, dur: 0, anims: anims,
      spinAt: function (t) { return per ? 360 * ((t / per) % 1) : 0; },
      breathAt: function () { return { s: 1, lx: 0, ly: 0 }; },
      flipAt: function (i, t) {
        var ph = phases[i];
        if (!(ph >= 0)) return 0;
        var u = t - ph;
        if (u <= 0) return 0;
        u = u % P;
        if (u < 2.4) return 0;
        if (u < 3.6) return 180 * FLIP.at((u - 2.4) / 1.2);
        if (u < 6.0) return 180;
        return 180 * (1 - FLIP.at((u - 6.0) / 1.2));
      }
    };
  };

  /**
   * Moments, in seconds from the start of the build, when card i's front is
   * turned away from the visitor: the instant the circles begin (the ring
   * was built showing its backs), and - with the ripple - just as the card
   * begins to turn over at the top, still a fifth of the way into its turn.
   * A push only slows the turn, so at that moment it can only leave the card
   * less far into its turn, never further: still facing away.
   */
  Wheel.prototype.faceAway = function (i) {
    if (!this.form) return [];
    var t = [this.form.total + 0.05];
    var f = this.sched && this.sched[i];
    if (f) t.push(this.form.total + f.s + this.tau * 0.3);
    return t;
  };

  /**
   * How long the wheel has been running, build included, in its own seconds -
   * read from the turn, which was booked in the same moment as the build and
   * slows with it under a push.
   */
  Wheel.prototype.elapsed = function () {
    var c = this.circ && this.circ.clock;
    var t = c && c.currentTime != null ? +c.currentTime / 1000 : 0;
    return t > 0 ? t : 0;
  };

  /** How far into the turn the wheel is, in the turn's own seconds. */
  Wheel.prototype.turnTime = function () {
    var c = this.circ;
    if (!c || !c.clock) return 0;
    var t = ((+c.clock.currentTime || 0) - c.delay * 1000) / 1000;
    if (!(t > 0)) return 0;
    return c.dur ? Math.min(c.dur, t) : t;
  };

  /**
   * The wheel's own motion, dialled back as the visitor leans on it. 0..1
   *
   * Every part of the turn is slowed by the same amount, so the ripple stays
   * on the top of the ring however hard they push.
   */
  Wheel.prototype.strain = function (v) {
    var c = this.circ;
    if (!c) return;
    var rate = Math.round(Math.max(0.05, 1 - Math.min(1, Math.abs(v)) * 0.95) * 100) / 100;
    if (rate === this.rate) return;
    this.rate = rate;
    for (var i = 0; i < c.anims.length; i++) {
      var a = c.anims[i];
      try {
        if (a.updatePlaybackRate) a.updatePlaybackRate(rate);
        else a.playbackRate = rate;
      } catch (e) {}
    }
  };

  /** Nothing of the wheel advances while the page is out of sight. */
  Wheel.prototype.hold = function (hidden) {
    for (var i = 0; i < this.anims.length; i++) holdAnim(this.anims[i], hidden);
  };

  /**
   * The wheel exactly as it stands this frame: every card's pose, with the
   * ripple folded into it, and the turn, the breath and the Gate's push.
   * Read from the plan, not from the screen - nothing here makes the page
   * stop and measure.
   */
  Wheel.prototype.now = function () {
    var gsap = window.gsap, f = this.form, n = this.cards.length;
    var tf = f.total;
    if (this.formAnim && this.formAnim.currentTime !== null) {
      tf = Math.min(f.total, Math.max(0, this.formAnim.currentTime / 1000));
    }
    var c = this.circ, tc = this.turnTime();
    var cards = [], faceO = [];
    for (var i = 0; i < n; i++) {
      var v = canon(f.parts[i].at(tf));
      v.ry += c ? c.flipAt(i, tc) : 0;
      cards.push(v);
      faceO.push(f.presence(i, tf));
    }
    var g = this.group;
    var read = function (k, d) {
      var v = gsap ? +gsap.getProperty(g, k) : NaN;
      return isFinite(v) ? v : d;
    };
    return {
      cards: cards,
      faceO: faceO,
      spin: c ? c.spinAt(tc) : 0,
      breath: c ? c.breathAt(tc) : { s: 1, lx: 0, ly: 0 },
      group: { x: read('x', 0), y: read('y', 0), r: read('rotation', 0), rx: read('rotationX', 0), sx: read('scaleX', 1), sy: read('scaleY', 1) }
    };
  };

  /** Everything the wheel has running, cancelled - nothing is left to finish. */
  Wheel.prototype.stop = function () {
    for (var i = 0; i < this.anims.length; i++) {
      try { this.anims[i].onfinish = null; this.anims[i].cancel(); } catch (e) {}
    }
    this.anims = [];
    this.circ = null;
    this.formAnim = null;
  };

  /**
   * The outro. The wheel does not disperse - it becomes the slider.
   *
   * These photographs are the same files, in the same order, that the slider
   * further down the page is built from. So the honest move is not to fade
   * them away into the logo: it is to unwind the ring into a rail in the
   * slider's own proportions and bring it down to where the slider stands.
   * What the visitor was looking at is what they arrive at, and nothing has
   * to say so.
   *
   *   STRAIGHTEN  the ring comes to its whole turn and every card stands
   *               upright and face-up, where it is.
   *   FORM        the circle opens into a rail, centre card largest, the rest
   *               stepping back in depth and turning to face it.
   *   LAND        (phones) the rail comes down onto the gallery. The piece the
   *               gallery shows in front lands exactly on its frame, and its
   *               two neighbours land as the slivers either side - at the
   *               gallery's own size, crop, zoom and sharpness - while the
   *               rest of the rail parts and dissolves. The ring has become
   *               the gallery, card for card.
   *   DESCEND     (wider screens, or nowhere to land) the rail tips away and
   *               sinks, handing the page to the slider on the way down.
   *
   * The beats OVERLAP: the rail begins to open before the cards are fully
   * upright, and begins to descend before it has fully opened, so it never
   * comes to a stop on the way - one movement with three turns in it, not
   * three movements.
   *
   * `onHandoff(mode)` and `onDone(mode)` are told how it ended: 'three' or
   * 'one' card landed, 'descend', or 'none' if there was no wheel to move.
   */
  Wheel.prototype.outro = function (o, onHandoff, onDone) {
    var cards = this.cards, n = cards.length;
    if (!this.started || !n || !this.form) {
      if (onHandoff) onHandoff('none');
      if (onDone) onDone('none');
      return;
    }

    var now = this.now();
    var line = o.line, form = o.form, drop = o.drop;
    var tForm = line * FORM_AT;
    var tLand = tForm + form * 0.72;
    var tDrop = tForm + form * 0.82;

    /* Where the photographs land, measured once, before anything moves. */
    var land = (o.land && o.slots && o.slots.length) ? o.land() : null;
    var slotOf = {};
    if (land) for (var si = 0; si < o.slots.length; si++) slotOf[o.slots[si].i] = o.slots[si];
    var mode = land ? (o.slots.length >= 3 ? 'three' : 'one') : 'descend';
    var mid = land ? o.slots[0].i : (n - 1) / 2;
    var span = Math.max(mid, n - 1 - mid) || 1;
    var K = 2;

    /* The wheel's centre on the screen. Everything that lands is aimed from it. */
    var box = this.scene ? this.scene.getBoundingClientRect() : null;
    var ox = box ? box.left + box.width / 2 : window.innerWidth / 2;
    var oy = box ? box.top + box.height / 2 : window.innerHeight / 2;
    var cw = cards[0].offsetWidth || 66, ch = cards[0].offsetHeight || cw * 1.5;

    /* GSAP's stagger, around a chosen card; `edges` runs it from the ends in. */
    var stag = function (i, c, each, edges) {
      var dmin = Infinity, dmax = 0;
      for (var j = 0; j < n; j++) {
        var dd = Math.abs(j - c);
        if (dd < dmin) dmin = dd;
        if (dd > dmax) dmax = dd;
      }
      var t = dmax > dmin ? (Math.abs(i - c) - dmin) / (dmax - dmin) : 0;
      return (edges ? 1 - t : t) * each * (n - 1);
    };

    this.stop();

    var parts = [], paints = [];

    /* The group lets go of the Gate's push; the turn and the breath settle. */
    var g = now.group;
    var gp = new Part(this.group, { x: g.x, y: g.y, r: g.r, rx: g.rx, sx: g.sx, sy: g.sy }, groupFrame);
    gp.to({ x: 0, y: 0, r: nearestTurn(wrap(g.r)), rx: 0, sx: 1, sy: 1 }, 0, line, 'power2.inOut');
    parts.push(gp);

    var a0 = wrap(now.spin);
    parts.push(new Part(this.spin, { a: a0 }, spinFrame).to({ a: nearestTurn(a0) }, 0, line, 'power2.inOut'));
    if (this.breath) {
      parts.push(new Part(this.breath, { s: now.breath.s, lx: now.breath.lx, ly: now.breath.ly || 0 }, breathFrame)
        .to({ s: 1, lx: 0, ly: 0 }, 0, line, 'power2.inOut'));
    }

    for (var i = 0; i < n; i++) {
      var v = now.cards[i];
      var r0 = wrap(v.r), ry0 = wrap(v.ry);
      var up = nearestTurn(r0), face = nearestTurn(ry0);
      var off = i - mid, a = Math.abs(off);
      var lean = Math.max(-K, Math.min(K, off));
      var p = new Part(cards[i], { x: v.x, y: v.y, z: v.z, r: r0, ry: ry0, rx: v.rx, s: v.s, o: 1 }, cardFrameO);

      /* STRAIGHTEN - the short way round, every angle (see wrap). */
      p.to({ r: up, ry: face, rx: 0, s: 1 }, stag(i, mid, 0.012), line, 'power2.inOut');

      /* FORM. Past two places out, everything is simply "away", at one angle:
         a coverflow turns its neighbours and then stops caring. */
      p.to({
        x: off * o.gap, y: 0, z: -Math.min(a, K + 1) * (o.depth / K),
        ry: face - (lean / K) * o.tilt,
        s: o.scale * (1 - Math.min(a / span, 1) * (1 - o.edge))
      }, tForm + stag(i, mid, 0.028), form, 'power3.inOut');

      var sl = land ? slotOf[i] : null;
      if (sl) {
        var side = sl.off ? land.side : 1;
        var sc = land.w * side / cw;
        p.to({ x: land.cx + sl.off * land.d - ox, y: land.cy - oy, z: 0, ry: face, s: sc }, tLand, drop, 'power3.inOut');
        this.dressLanding(cards[i], sl, land, now.faceO[i], sc, cw, ch, tLand, drop, parts, paints);
      } else if (land) {
        /* The rest of the rail parts to either side and is gone by mid-flight. */
        p.by({ x: off < 0 ? -48 : 48 }, tLand, drop * 0.55, 'power2.out');
        p.to({ o: 0 }, tLand, drop * 0.55, 'power2.out');
      } else {
        p.to({ o: 0 }, tDrop + drop * o.fadeAt + stag(i, mid, o.stream, true), drop * (1 - o.fadeAt) + 0.18, 'power2.in');
      }
      parts.push(p);

      /* The faces keep the presence the build gave them; the ripple's turn is
         now the card's own, so the flip comes to rest at none. */
      if (!sl) {
        for (var k = 0; k < this.faces[i].length; k++) this.faces[i][k].style.opacity = now.faceO[i].toFixed(3);
      }

      /* A card already facing front never shows its back again: the rail and
         the landing are all fronts. Its back photograph is taken out of the
         drawing, so the outro - where every card is scaled up and drawn
         again at its new size - has half as many pictures to draw. */
      var dev = Math.abs(ry0 - face);
      if (dev > 180) dev = 360 - dev;
      if (dev < 80) {
        for (var bk = 0; bk < this.faces[i].length; bk++) {
          if (this.faces[i][bk].classList.contains('lmat__face--back')) this.faces[i][bk].style.visibility = 'hidden';
        }
      }
    }

    if (!land) {
      /* DESCEND - the group carries the rail: one rigid object going down,
         not a scatter of pictures that happen to move the same way. */
      gp.to({ y: window.innerHeight * o.fall, rx: -o.lay, sx: 0.88, sy: 0.88 }, tDrop, drop, 'power2.in');
    }

    var total = 0;
    for (var q = 0; q < parts.length; q++) total = Math.max(total, parts[q].end());
    for (q = 0; q < parts.length; q++) {
      var pt = parts[q];
      if (pt.moves.length) this.play(pt, total, 50, 0);
      else {
        var fr = pt.fmt(pt.pose);
        if (fr.transform) pt.el.style.transform = fr.transform;
        if (fr.opacity) pt.el.style.opacity = fr.opacity;
      }
    }
    for (q = 0; q < paints.length; q++) {
      this.keep(paints[q].el.animate(paints[q].frames, {
        duration: drop * 1000, delay: tLand * 1000, easing: 'cubic-bezier(0.76, 0, 0.24, 1)', fill: 'both'
      }));
    }

    /*
     * The page is handed over on the motion's own timeline, so a busy page
     * cannot make it arrive early or late against what is on the screen.
     *
     * Landing: at 86% of the landing, when every card is within a pixel of
     * its frame. The gallery is shown under them there, and they dissolve
     * into it as they finish arriving - a card is drawn through the 3D scene
     * and the gallery flat, and the two can differ by a pixel; met in motion,
     * that pixel is part of the settle, not a jump after it.
     * Descent: at 88% of the fall.
     */
    var handAt = land ? tLand + drop * 0.86 : tDrop + drop * 0.88;
    this.timer(handAt, function () { if (onHandoff) onHandoff(mode); });
    this.timer(Math.max(total, handAt) + 0.02, function () { if (onDone) onDone(mode); });
  };

  /**
   * A landing card made into the gallery's own picture.
   *
   * Its face is squashed from the card's 2:3 to the gallery's 4:5 (scaleY on
   * the face, its photograph stretched back by the inverse, so the picture
   * is CROPPED, not squashed); the photograph is brought to the gallery's
   * zoom and, for a neighbour, slid by the gallery's parallax; and it comes
   * fully opaque. The corners and the shadow are paint, not motion - they
   * are given to the page's thread, where a late frame cannot be seen.
   *
   * The photograph was laid out at its own shape and given the gallery's own
   * file while the mark was still on the bronze (Entrance.dress), so what
   * arrives is not a smaller copy of the gallery's picture, enlarged: it is
   * the gallery's picture.
   */
  Wheel.prototype.dressLanding = function (card, sl, land, faceO, sc, cw, ch, at, dur, parts, paints) {
    var faces = card.querySelectorAll('.lmat__face');
    var front = card.querySelector('.lmat__face--front');
    for (var b = 0; b < faces.length; b++) if (faces[b] !== front) faces[b].style.opacity = faceO.toFixed(3);
    if (!front) return;

    /* The bronze behind the photograph is there so an unloaded card is never
       a white hole. A landing card's photograph is on the machine, and that
       bronze only ever showed in the softened pixels of its clipped edge - a
       dark hairline round a picture the gallery draws without one. */
    front.style.backgroundColor = 'transparent';
    var pic = front.querySelector('img');
    if (pic) pic.style.backgroundColor = 'transparent';

    var kk = (land.h / land.w) * (cw / ch);
    parts.push(new Part(front, { k: 1, o: faceO }, faceFrame).to({ k: kk, o: 1 }, at, dur, 'power3.inOut'));

    var img = front.querySelector('img');
    if (img) {
      var box = img.__lmBox || { w: cw, h: ch, a: cw / ch };
      var winW = cw, winH = kk * ch;
      var coverW = box.a >= winW / winH ? winH * box.a : winW;
      parts.push(new Part(img, { tx: 0, m: 1, k: 1 }, imgFrame).to({
        tx: -sl.off * land.par * winW,
        m: coverW * land.zoom / box.w,
        k: kk
      }, at, dur, 'power3.inOut'));
    }

    var cs = window.getComputedStyle(front);
    var rx = land.radius / sc, ry = land.radius / (sc * kk);
    paints.push({
      el: front,
      frames: [
        { borderRadius: cs.borderRadius, boxShadow: cs.boxShadow },
        { borderRadius: rx.toFixed(3) + 'px / ' + ry.toFixed(3) + 'px', boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px' }
      ]
    });
  };

  Wheel.prototype.destroy = function () {
    this.stop();
  };

  /* -------------------------------------------------------------- the gate */

  /**
   * THE DOOR IS THE WHEEL.
   *
   * The page must not move until the photographs have been sent to the
   * slider, or the visitor arrives at the slider before the thing that makes
   * it. But a page that simply refuses to scroll is a broken page - the
   * gesture goes in and nothing at all comes out, which is the one reading we
   * cannot afford.
   *
   * So the scroll is not refused. It is ABSORBED, and spent on the object
   * that is in the way.
   *
   *   push      what the visitor is asking for, 0..1, decaying when they stop
   *   v         what is drawn, on a spring behind push - the overshoot on the
   *             way back is the whole character of it
   *
   * Everything the visitor sees is v:
   *
   *   the ring sinks, and squashes toward the horizontal - the first hint of
   *     the rail it is about to become, held under their thumb and let go of
   *     if they stop
   *   its spin and its flipping slow to almost nothing, so the object is
   *     visibly taking the strain
   *   the mark lifts, the opposite way - both destinations already separating
   *     before either has left
   *   the bronze breathes back in over the live site, so the threshold is
   *     visibly still closed rather than merely unresponsive
   *
   * Push far enough and it commits. The door does not refuse; it opens
   * because it was leaned on. And the push is not thrown away when it does -
   * see Entrance.glide.
   */
  function Gate(ent, o) {
    var self = this;

    this.ent = ent;
    this.o = o;
    this.ground = ent.root.querySelector('.lmat__ground');

    this.push = 0;
    this.v = 0;
    this.vel = 0;
    this.banked = 0;      /* downward pixels absorbed, spent on release */
    this.armed = false;   /* may it commit - false until the wheel is formed */
    this.open = false;
    this.last = 0;
    this.lastIn = 0;
    this.touchY = null;
    this.raf = 0;

    this.onWheel = function (e) {
      /* ctrl+wheel is a pinch zoom, not a scroll. Never take that away. */
      if (e.ctrlKey) return;
      /* Non-passive on purpose. This is the scroll block itself: overflow
         hidden alone does not stop a trackpad chaining to the document on
         every engine, and it does nothing at all about pull-to-refresh. */
      if (e.cancelable) e.preventDefault();
      var d = e.deltaY;
      if (e.deltaMode === 1) d *= 16;            /* lines */
      else if (e.deltaMode === 2) d *= window.innerHeight;  /* pages */
      self.feed(d, false);
    };

    this.onTouchStart = function (e) {
      self.touchY = e.touches && e.touches.length ? e.touches[0].clientY : null;
    };

    this.onTouchMove = function (e) {
      if (e.cancelable) e.preventDefault();
      if (!e.touches || !e.touches.length) return;
      var y = e.touches[0].clientY;
      if (self.touchY !== null) self.feed(self.touchY - y, true);
      self.touchY = y;
    };

    this.onTouchEnd = function () { self.touchY = null; };

    this.onKey = function (e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      var k = e.key;
      /* Tab is somebody navigating with a keyboard. They are not admiring
         the wheel, they are trying to use the site - let them straight in. */
      if (k === 'Tab') { self.commit(); return; }
      /*
       * Only the keys that actually scroll. Enter and Escape used to be
       * swallowed here too, which meant that for the two seconds of the
       * outro - the door has opened, but the block is still up - a keyboard
       * visitor could not activate the link they had just tabbed to.
       */
      if (k === ' ' || k === 'Spacebar' || k === 'PageDown' ||
          k === 'ArrowDown' || k === 'End') {
        if (e.cancelable) e.preventDefault();
        self.feed(self.o.effort * 0.4, false);
        return;
      }
      if (k === 'PageUp' || k === 'ArrowUp' || k === 'Home') {
        if (e.cancelable) e.preventDefault();
        self.feed(-self.o.effort * 0.4, false);
      }
    };

    /* Impatience is allowed. A click on the threshold means "enough" - but
       not a click on something the visitor is actually trying to use, which
       is a request to navigate, not a request to open this. */
    this.onPointer = function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('a,button,input,select,textarea,[role="button"]')) return;
      /*
       * Not before the ring exists. On a phone EVERY touch is a pointerdown -
       * the start of a swipe included - so this used to open the door on
       * the bronze itself: the outro ran before reveal(), its clearTimers()
       * cancelled the reveal, and the room never got its word (lmat-veiled
       * stayed on, the hero's name waited for its 10s fallback). Before the
       * gate is armed a touch is answered by the push alone; a tap still
       * reaches the root's own click, which brings the reveal forward.
       */
      if (!self.armed) { self.wanted = true; return; }
      self.commit();
    };

    this.bind();
    this.loop = function (now) { self.tick(now); };
    this.raf = window.requestAnimationFrame(this.loop);
  }

  Gate.prototype.bind = function () {
    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('touchstart', this.onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('touchend', this.onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    window.addEventListener('keydown', this.onKey);
    document.addEventListener('pointerdown', this.onPointer);
  };

  Gate.prototype.unbind = function () {
    window.removeEventListener('wheel', this.onWheel, { passive: false });
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove, { passive: false });
    window.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('touchcancel', this.onTouchEnd);
    window.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onPointer);
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.raf = 0;
  };

  /**
   * A gesture, in pixels. Down is positive.
   *
   * A finger covers far more ground than a wheel notch for the same intent,
   * so touch is asked for less. Scrolling UP cannot open the door - it pushes
   * back against it, and the ring lifts instead of sinking, which is the only
   * honest answer to a gesture aimed the other way.
   */
  Gate.prototype.feed = function (px, touch) {
    if (this.open || !px) return;

    /* The loop parks itself when there is nothing left to draw. Waking it is
       the first thing a gesture does. */
    if (!this.raf) { this.last = 0; this.raf = window.requestAnimationFrame(this.loop); }

    var effort = this.o.effort * (touch ? 0.62 : 1);
    this.push += px / effort;

    if (px > 0) this.banked += px;

    /*
     * Until the wheel exists there is nothing to open. Someone scrolling at
     * half a second is looking at bronze - the load window is the load
     * window, and it is not something to be skipped past. The push still
     * registers, so the gesture is answered, but it is held short of the
     * threshold so that arming cannot fire it retroactively.
     */
    var ceiling = this.armed ? 1 : 0.72;
    if (this.push > ceiling) this.push = ceiling;
    if (this.push < -0.34) this.push = -0.34;

    this.lastIn = this.last;
    if (this.armed && this.push >= 1) this.commit();
  };

  /** The wheel has formed and the ground has gone. Now it can be opened. */
  Gate.prototype.arm = function () {
    this.armed = true;
    /*
     * Somebody already touched it while it was still forming. They asked to
     * go in; they should not have to ask twice. The room is given a beat to
     * be seen (the ground lifting), and then the door opens on its own.
     */
    if (this.wanted && !this.open) {
      var self = this;
      this.ent.at(700, function () { self.commit(); });
    }
  };

  Gate.prototype.tick = function (now) {
    var dt = this.last ? Math.min(0.05, (now - this.last) / 1000) : 0.016;
    this.last = now;

    /*
     * The ask fades once the visitor stops asking. Not instantly - a trackpad
     * delivers a gesture as a burst of events with gaps in it, and a gate
     * that let go between them would never accumulate anything.
     */
    if (now - this.lastIn > 120 && this.push !== 0) {
      this.push += (0 - this.push) * (1 - Math.exp(-dt / 0.19));
      if (Math.abs(this.push) < 0.002) this.push = 0;
    }

    /*
     * And the drawing follows the ask on a spring rather than tracking it.
     * Underdamped on purpose - about nine percent overshoot - so letting go
     * returns the ring with a single soft wobble instead of a snap. That
     * wobble is the difference between an object with weight and a slider.
     */
    var k = 150, c = 15;
    this.vel += ((this.push - this.v) * k - this.vel * c) * dt;
    this.v += this.vel * dt;

    this.render();

    if (this.open) return;

    /*
     * At rest, stop. The threshold can stand here for a good while, and a
     * frame loop writing an identity transform sixty times a second the
     * whole time is a heater. feed() wakes it.
     */
    if (this.push === 0 && Math.abs(this.v) < 0.0006 && Math.abs(this.vel) < 0.0006) {
      this.v = this.vel = 0;
      this.render();
      this.raf = 0;
      return;
    }

    this.raf = window.requestAnimationFrame(this.loop);
  };

  Gate.prototype.render = function () {
    var gsap = window.gsap;
    var v = this.v;
    var ent = this.ent;

    if (gsap && ent.wheel.group) {
      /*
       * The ring gives. It sinks under the push and squashes toward the
       * horizontal, which is the first few percent of the rail it becomes -
       * so what the visitor is doing and what the door does are the same
       * move, stopped early. One transform on one element: the cards are
       * left entirely alone, because their own pivot is offset and anything
       * written per-card here would fight the outro.
       */
      gsap.set(ent.wheel.group, {
        y: v * this.o.give,
        scaleX: 1 + v * 0.13,
        scaleY: 1 - v * 0.17,
        rotationX: -v * 7
      });
      ent.wheel.strain(v);
    }

    /* The mark goes the other way. The two destinations of the outro are
       already separating, before either has left. */
    if (gsap && ent.mark && ent.mark.classList.contains('is-settled')) {
      gsap.set(ent.mark, { y: -v * this.o.lift, scale: 1 - v * 0.055 });
    }

    /*
     * The bronze breathes back in. The ground has already gone; bringing a
     * little of it back under the push says the threshold is still closed,
     * without a single piece of interface saying so. Still one flat colour -
     * there is no gradient here and there must never be one.
     */
    if (this.ground && this.loose) {
      var w = Math.max(0, v) * this.o.wash;
      this.ground.style.opacity = w > 0.001 ? String(w) : '';
    }
  };

  /** The ground has finished lifting, so the wash may be driven per frame. */
  Gate.prototype.loosen = function () {
    if (!this.ground) return;
    this.ground.style.transition = 'none';
    this.loose = true;
  };

  Gate.prototype.commit = function () {
    if (this.open) return;
    this.open = true;
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.raf = 0;

    var gsap = window.gsap;
    if (this.ground && this.loose && gsap) {
      gsap.to(this.ground, { opacity: 0, duration: 0.45, ease: 'power2.out' });
    } else if (this.ground) {
      this.ground.style.opacity = '';
    }

    this.ent.outro(this.banked);
  };

  /* ------------------------------------------------- the brand, afterwards */

  /**
   * Once the mark has landed it IS the header's brand - and from that moment
   * the header owns it.
   *
   * The header renders its own copy of this mark in its own name slot, and
   * decides for itself when the mark shows and when the word does (see THE
   * BAR in sections/header.liquid). That copy waits unseen under
   * `html.lmat-brand-held` while this one is still in the air. The flight
   * aims at that copy's exact rectangle; on landing the hold is lifted and
   * this one lets go in the same instant, so the hand-over is the same
   * pixels in the same place - never one, never two.
   *
   * This used to keep the flown mark alive for the rest of the visit, fixed,
   * re-measuring the header on every scroll to decide whether to show. The
   * header it measured was itself sliding in and out, so the answer changed
   * as the bar moved; and on every later visit in the session, when there
   * is no entrance, there was no mark in the bar at all. One mark, owned by
   * the bar, on every visit, replaces both.
   */
  function Brand(mark, opts) {
    this.mark = mark;
    this.opts = opts;
    this.slot = document.querySelector('.lh-logo__mark');
    this.logo = document.querySelector('.lh-logo');
    this.landed = false;
    /* In flight it is simply visible, wherever the tween has it. */
    this.mark.classList.add('is-brand-on');
  }

  /** Where it lands: exactly on the header's own mark. */
  Brand.prototype.home = function () {
    var r = null;
    if (this.slot) {
      r = this.slot.getBoundingClientRect();
      if (!r.width && !r.height) r = null;
    }
    if (r) return { left: r.left, top: r.top, size: r.width };

    /* A bar with no mark in it: aim for the centre of the name, as before. */
    var size = this.opts.brandSize;
    var l = this.logo && this.logo.getBoundingClientRect();
    if (l && (l.width || l.height)) {
      return {
        left: l.left + l.width / 2 - size / 2,
        top: l.top + l.height / 2 - size / 2 + this.opts.lift,
        size: size
      };
    }
    return { left: window.innerWidth / 2 - size / 2, top: 18, size: size };
  };

  /**
   * Arrived. The bar's mark is shown and this one released in the same
   * synchronous block - the header answers `loomish:brand` by applying it
   * with no transition - so no frame is ever painted with both or neither.
   */
  Brand.prototype.land = function () {
    if (this.landed) return;
    this.landed = true;
    document.documentElement.classList.remove('lmat-brand-held');
    say('loomish:brand');
    this.mark.classList.remove('is-brand-on');
    this.mark.classList.add('is-home');
  };

  /* Whatever takes the entrance apart, the bar must not be left without its mark. */
  Brand.prototype.destroy = function () {
    this.land();
  };

  /* ---------------------------------------------------------- the threshold */

  function Entrance(root) {
    var self = this;
    var d = root.dataset;

    this.root = root;
    live.push(this);
    root.__lmat = this;

    this.mark = root.querySelector('.lmat__mark');
    this.editor = d.editor === '1';
    this.dissolve = d.after === 'dissolve';
    this.mobile = window.matchMedia('(max-width: 749px)').matches;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timers = [];
    this.done = false;
    this.holding = this.dissolve && !this.editor && !this.reduce;
    /*
     * Off by default, and that is a correction rather than an omission. A
     * carry of any size scrolls the visitor past 80px, which is where the
     * hero wordmark begins its scroll fade - so the name that had just
     * risen out of the clearing bronze was wiped a second later by the
     * page moving underneath it. The push is already answered, at length,
     * by the outro; it does not also need to be paid in travel.
     */
    this.carry = (+d.releaseCarry || 0) / 100;

    /*
     * THE RING FITS THE SCREEN (corrected 2026-10-21).
     *
     * The old fit held r2 inside the screen - but the circle's radius is
     * r1 + r2, and each card stands on it pointing outward, so it reaches a
     * further three quarters of a card. On a 390px phone the side cards were
     * cut by the screen's edges on every visit, and the ring's off-centre
     * turn swung them further out. Now the whole ring - its outermost corner,
     * at the height of its breath - stays inside the shorter side of the
     * screen. A screen with room for the section's own radius gets exactly
     * that; only a smaller one draws the ring in.
     */
    var r1 = this.mobile ? +d.radius1M : +d.radius1;
    var r2 = this.mobile ? +d.radius2M : +d.radius2;
    var breath = Math.max(0, +d.breath || 0) / 100;
    var lean = Math.max(0, +d.lean || 0);
    var cwPx = parseFloat(window.getComputedStyle(root).getPropertyValue('--cw')) || (this.mobile ? 66 : 96);
    var reach = (Math.min(window.innerWidth, window.innerHeight) / 2 - (this.mobile ? 10 : 28)) /
      (1 + breath + lean * 0.002);
    var R = Math.sqrt(Math.max(0, reach * reach - cwPx * cwPx / 4)) - cwPx * 0.75;
    r2 = Math.min(r2, Math.max(48, Math.round(R - r1)));

    /*
     * Two circles, then in: the threshold opens by itself once the formed
     * wheel has turned its circles (see Wheel.circles). Nobody has to touch
     * or scroll; a push still opens it early.
     */
    this.auto = (d.autoOpen === '1' && +d.circles > 0)
      ? { n: Math.round(+d.circles), t: Math.max(0.6, +d.circlesTime || 2.2) }
      : null;

    this.wheel = new Wheel(root, {
      radius1: r1,
      radius2: r2,
      stagger: +d.cardStagger / 100,
      /* A phone is held in a hand, often on the move: the same ring, the same
         shape, played a quarter faster there. Desktop keeps the set tempo. */
      speed: ((+d.wheelSpeed || 100) / 100) * (this.mobile ? 1.25 : 1),
      spin: +d.wheelSpin,
      flip: d.cardFlip === '1',
      circles: this.auto ? this.auto.n : 0,
      circlesTime: this.auto ? this.auto.t : 0,
      breath: breath,
      lean: lean,
      ripple: d.ripple !== '0'
    });

    /* Where the photographs will land on a phone - worked out while the mark
       is still on the bronze (see whenPictures). */
    this.plan = null;
    this.three = d.landThree !== '0';

    this.revealed = false;
    root.classList.add('is-ready');

    /*
     * The mark's arrival is a CSS animation with a forwards fill, and a
     * forwards fill outranks an inline transform - so nothing may be written
     * to the mark until it has finished. The class hands ownership over.
     */
    if (this.mark) {
      var settle = function () { self.mark.classList.add('is-settled'); };
      this.mark.addEventListener('animationend', settle, { once: true });
      this.at((+d.markDelay || 0) * 1000 + 1700, settle);
    }

    /*
     * ONE   the mark alone on the ground, while the site loads behind it.
     * TWO   the photographs rise around it into the wheel.
     * THREE the ground goes, and the wheel is left turning OVER the live
     *       site — which is still held, but held by something that answers.
     * FOUR  the visitor leans on it; the ring opens into a rail and goes down
     *       to the slider while the mark goes up into the header, and the page
     *       is given back the moment the photographs arrive.
     */

    /*
     * Reduced motion gets the threshold without the theatre: the mark, and
     * then the site. No wheel is built at all — a request not to animate is
     * not a request for a shorter animation — and nothing is held, because
     * there is no animation for a hold to protect.
     */
    if (this.reduce) {
      this.at(700, function () { self.reveal(); });
      return;
    }

    /*
     * THE DOOR IS BUILT ON THE FIRST FRAME, though it cannot be opened yet.
     *
     * The gesture is CAUGHT from the start - overflow:hidden alone leaks on
     * more than one engine, and does nothing whatsoever about pull-to-
     * refresh, so a hard scroll over the bronze could bounce the whole
     * document. From here nothing reaches the document at all.
     *
     * It is only ANSWERED once there is something on screen to answer with:
     * the mark from the moment it settles, the ring once it has formed. The
     * bronze itself is a loading window and does not need to react to being
     * pushed, but it must not stutter either.
     */
    if (this.holding) {
      var give = (this.mobile ? +d.gateGiveM : +d.gateGive) || (this.mobile ? 30 : 48);
      this.gate = new Gate(this, {
        effort: +d.gateEffort || 380,
        give: give,
        /* The mark goes the other way, proportionately - so a section tuned
           for a gentler ring gets a gentler lift with it, from one control. */
        lift: give * 0.46,
        wash: (+d.gateWash || 18) / 100
      });
    }

    /*
     * THE WHEEL WAITS FOR ITS PICTURES.
     *
     * logo_hold is a MINIMUM, not a cue. A wheel of empty rectangles is worse
     * than a slightly longer wait, and holding the mark on the bronze is
     * exactly what that window is for — so TWO begins when the hold has
     * elapsed AND the photographs have arrived, whichever is later.
     *
     * This is why it used to look broken on a cold cache and fine on a warm
     * one: the timer had no idea whether there was anything to show.
     */
    this.whenPictures(+d.logoHold * 1000, +d.picCap * 1000, function (enough) {
      /*
       * TOO SLOW FOR THE PHOTOGRAPHS (2026-10-25). Out of time with a
       * quarter of them or more still on their way, the opening does not
       * build a wheel of empty frames that fill in as it turns. It bows out:
       * once the room is there, the bronze lifts and the mark rises into the
       * header - the same exit as ever, without the wheel. Only a connection
       * this slow ever sees it.
       */
      if (enough === false) {
        self.whenRoom(function () { self.outro(0); });
        return;
      }
      root.classList.add('is-wheeling');

      /*
       * THREE waits for TWO to actually finish, rather than for a number
       * somebody typed. wheel_hold is the pause after the wheel has formed,
       * which is what it always read as and never was.
       */
      /*
       * The circles begin the instant the ring has closed - booked on the
       * compositor with the build itself (Wheel.run) - and the room comes up
       * behind them: by the second circle the ring is turning over the
       * photograph, not over bronze. When they finish, it opens.
       */
      self.wheel.run(function () {
        /* The circle has closed: what is drawn now only moves. */
        self.formed = true;
        if (self.decodeGallery) self.decodeGallery();
        self.at(+d.wheelHold * 1000 * (self.mobile ? 0.6 : 1), function () {
          /* THE GROUND WAITS FOR THE ROOM. Only on this path — every
             ceiling below calls reveal() directly, so nothing here can
             hold the door shut. */
          self.whenRoom(function () { self.reveal(); });
        });
      }, self.auto ? function () { self.autoOpen(); } : null);
      /* Booked in the same moment as the wheel, so on the same clock. */
      self.armSwaps();

      /* A ceiling, in case the timeline never reports back. The ground must
         lift whatever happens; a threshold that sticks is a closed door. */
      self.at(self.wheel.formTime() * 1000 + +d.wheelHold * 1000 + 900,
              function () { self.reveal(); });
    });

    /* And a ceiling over the whole thing, however slow the pictures are. */
    this.at((+d.picCap + +d.wheelHold) * 1000 + this.wheel.formTime() * 1000 + 1500,
            function () { self.reveal(); });

    // Impatience is allowed: a click or a key skips straight to the reveal
    root.addEventListener('click', function () { self.reveal(); });
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') { e.preventDefault(); self.reveal(); }
    });
  }

  /**
   * Every act of the entrance is scheduled here, and here is visible time.
   *
   * Not one `setTimeout` remains in this file. That is deliberate and it is
   * load-bearing: a wall-clock timer anywhere in the sequence reintroduces
   * the second clock, and with it every desync a hidden tab can cause.
   */
  Entrance.prototype.at = function (ms, fn) {
    this.timers.push(clock.at(ms, fn));
  };

  /**
   * Call back once the wheel's photographs are on the machine AND DECODED,
   * and the hold has elapsed - whichever takes longer.
   *
   * DECODED (2026-10-21). Loaded only means the bytes have arrived. A picture
   * is turned into pixels the first time it is drawn - on a phone, inside the
   * very frame that first shows it - so every card that rose into the build
   * was paying for its own decode on the frame it appeared (measured on a mid
   * phone: not one front decoded when the ring began). decode() does that
   * work now, off the screen, while the mark is still the only thing on it.
   *
   * The backs are waited on as well now. The ring is built showing them -
   * they were never seen before (see Wheel.formation) - so they are the first
   * photographs the circle shows.
   *
   * `cap` is the promise that this can never become a locked door: however bad
   * the connection, the wheel starts and the ground lifts behind it.
   */
  Entrance.prototype.whenPictures = function (minMs, capMs, done) {
    var self = this;
    /*
     * ONLY THE WHEEL'S OWN PHOTOGRAPHS HOLD THE WHEEL (2026-10-21).
     *
     * The founder's direction (2026-10-15) stands: the logo alone for two
     * seconds, "and that time should be used for all the website and the
     * slider cards to load" - and everything the opening will show is asked
     * for from this moment. But the wheel used to WAIT for all of it: the
     * room's photograph and the gallery's pieces, two megabytes on a phone,
     * none of it on screen for seconds yet. On a 4G connection the mark
     * stood alone for six seconds, out to its ceiling, every visit.
     *
     * Each picture is now waited on where it is first seen. The wheel waits
     * for its own photographs (a few hundred kilobytes). The room is waited
     * on by the reveal (whenRoom), which comes after the build. The gallery's
     * pieces are waited on by the landing: its photographs dissolve only
     * once the gallery's own are painted under them (galleryReady). Nothing
     * the visitor sees is ever an empty frame; the mark simply stops waiting
     * on pictures it is not about to show.
     */
    var imgs = Array.prototype.slice.call(this.root.querySelectorAll('[data-lmat-img], [data-lmat-back]'));

    /*
     * THE GALLERY, IN THE ORDER IT IS NEEDED (2026-10-21, 2026-10-25).
     *
     * Hidden pieces wait for a scroll that a held page never makes, so the
     * gallery is asked for them - in the order they are needed. The wheel's
     * own photographs first. Then the pieces the rail lands on, once the
     * wheel's are in: asked for sooner, they only slowed the wheel's. Then
     * the rest, once those are in. While the opening holds the gallery, only
     * the pieces marked to land are even drawn (data-lmat-land): on a phone
     * every piece stands in the same place, and the browser counted all
     * twelve as on screen and fetched two megabytes at once, in front of the
     * wheel and the room (sections/loomish-slider-ad.liquid).
     *
     * And the gallery is read only once it has been read whole. This runs
     * ahead of the page now (AHEAD OF THE PAGE), and a gallery half-parsed
     * would plan a landing on half a gallery. If it is not whole yet the
     * wheel goes ahead without it, and it is planned the moment it is - the
     * landing cards laid out then, and their swaps booked for whichever of
     * their moments are still to come (armSwaps).
     */
    var landing = [], later = [];
    var onwards = false, planned = !this.mobile;
    var askLater = this.askLater = function () {
      for (var li = 0; li < later.length; li++) { try { later[li].loading = 'eager'; } catch (e) {} }
      later = [];
    };
    var askLanding = function () {
      for (var i = 0; i < landing.length; i++) {
        var slide = landing[i].closest ? landing[i].closest('.swiper-slide') : null;
        if (slide) slide.setAttribute('data-lmat-land', '');
        try { landing[i].loading = 'eager'; } catch (e) {}
      }
      landing = [];
    };
    /* The gallery's turn: the pieces it lands on - or, with nothing to land
       on, all of it. */
    var gallery = function () {
      if (!onwards || !planned) return;
      if (self.plan) askLanding();
      else askLater();
    };
    var plan = function () {
      if (planned) return;
      var gal = document.querySelector('.loomish-slider-ad[data-phone-engine="rail"]');
      if (gal ? !closed(gal) : document.readyState === 'loading') return;
      planned = true;
      var fronts = gal ? Array.prototype.slice.call(gal.querySelectorAll('.swiper-slide:not([data-lsa-clone]) .loomish-slider-ad__image')) : [];
      var p = self.plan = self.landingPlan(fronts);
      fronts.forEach(function (im) {
        var lands = p ? p.slots.some(function (s) { return s.img === im; }) : false;
        (lands ? landing : later).push(im);
      });
      if (p) self.watchGallery(p);
      gallery();
      if (onwards) self.layoutLanding();
      if (self.wheel.started) self.armSwaps();
    };
    var poll = function () { plan(); if (!planned) clock.at(250, poll); };
    poll();
    /* The wheel's own photographs are in, or their time is up. */
    var onward = function () {
      if (onwards) return;
      onwards = true;
      plan();
      gallery();
    };
    /*
     * Visible time here too. `load` fires on an image whether anybody is
     * looking or not, so measuring the hold against the wall clock meant that
     * a tab hidden while the photographs were still arriving came back with
     * `minMs` long since elapsed — and the mark, which is the whole of the
     * opening beat, was skipped entirely.
     */
    var t0 = clock.t;
    var fired = false;

    /*
     * THE MARK'S SECONDS START WHEN IT IS SEEN (2026-10-15).
     *
     * The bronze and the mark are painted by the page itself, before this
     * script can run - a cold phone load measured it starting 3.5s in when it
     * was still deferred. Counting the hold from here made "two seconds of
     * the logo" more than five. So the time the mark has already been on screen
     * (since the first paint) is counted too - but only for a page that has
     * never been out of sight, because a hidden tab's seconds were seen by
     * nobody and the visible clock owes them to the visitor.
     */
    var spent = 0;
    try {
      if (!window.__lmatWasHidden && !document.hidden && window.performance && performance.getEntriesByType) {
        var paints = performance.getEntriesByType('paint');
        for (var pi = 0; pi < paints.length; pi++) {
          if (paints[pi].name === 'first-paint') spent = Math.max(0, performance.now() - paints[pi].startTime);
        }
      }
    } catch (e) {}

    var go = function () {
      if (fired) return;
      fired = true;
      onward();
      /* And whatever happens to the landing's pictures, the rest of the
         gallery is asked for well before the gallery could need it - on the
         clock itself, not self.at(): the reveal clears those. */
      clock.at(4000, askLater);
      /* Enough of the wheel's photographs to build it with? A quarter or
         more still missing and the opening bows out instead (the
         constructor: TOO SLOW FOR THE PHOTOGRAPHS). */
      var have = 0;
      for (var hi = 0; hi < imgs.length; hi++) if (imgs[hi].complete && imgs[hi].naturalWidth) have++;
      var enough = !imgs.length || have >= imgs.length * 0.75;
      var fin = function () { done(enough); };
      var left = Math.max(0, minMs - (clock.t - t0) - spent);
      if (left) self.at(left, fin);
      else fin();
    };

    /*
     * The wheel's photographs are here: the landing cards are laid out at
     * their pictures' own shape (no visible change), and - THE ROOM IS
     * DECODED BEFORE THE WHEEL MOVES. Its photograph is the largest picture
     * on the page, and one that has arrived but not been turned into pixels
     * is decoded on the very threads that draw the moving wheel: measured,
     * the build stalled for a third of a second on a mid phone. So the wheel
     * waits for that decode - and, if the room is still on its way, for a
     * moment more, never for a slow download. After that the reveal waits
     * for it on its own (whenRoom).
     */
    var ready = function () {
      onward();
      self.layoutLanding();
      /* Not read yet (AHEAD OF THE PAGE): nothing to decode, and nothing to
         wait for here - the reveal waits for it (whenRoom). */
      var room = document.getElementById('loomish-hero-bg-img');
      if (!room) { go(); return; }
      var once = false;
      var on = function () { if (once) return; once = true; go(); };
      var decode = function () {
        if (room.decode) room.decode().then(on, on);
        else on();
      };
      if (room.complete && room.naturalWidth) { decode(); return; }
      room.addEventListener('load', decode, { once: true });
      room.addEventListener('error', on, { once: true });
      self.at(800, on);
    };

    /* The ceiling counts the mark's seen seconds too: however late this
       began, the mark never stands alone for longer than the cap. */
    this.at(Math.max(250, capMs - spent), go);
    if (!imgs.length) { ready(); return; }

    var waiting = imgs.length;
    var tick = function () { if (--waiting <= 0) ready(); };

    imgs.forEach(function (im) {
      var decoded = function () {
        if (im.decode) im.decode().then(tick, tick);
        else tick();
      };
      if (im.complete && im.naturalWidth) { decoded(); return; }
      im.addEventListener('load', decoded, { once: true });
      /* A picture that fails is a picture that has stopped being waited for */
      im.addEventListener('error', tick, { once: true });
    });
  };

  /**
   * Where the photographs will land on a phone - the wheel card nearest its
   * middle that the gallery also holds (the piece the gallery will show in
   * front), and the wheel cards holding the gallery's pieces either side of
   * it. Made early, so the mark's seconds can be spent fetching exactly those
   * pictures, and so the landing cards can be dressed in them (dress).
   */
  Entrance.prototype.landingPlan = function (fronts) {
    var cards = this.wheel.cards;
    if (!cards.length || !fronts.length) return null;
    var keys = fronts.map(function (im) { return keyOf(im.getAttribute('src')); });
    var cardKey = function (c) {
      var im = c.querySelector('.lmat__face--front img');
      return im ? keyOf(im.getAttribute('src')) : '';
    };
    var middle = (cards.length - 1) / 2;
    var order = cards.map(function (c, i) { return i; }).sort(function (a, b) {
      return (Math.abs(a - middle) - Math.abs(b - middle)) || (b - a);
    });
    var pick = -1, k = -1;
    for (var oi = 0; oi < order.length && pick < 0; oi++) {
      var kk = keys.indexOf(cardKey(cards[order[oi]]));
      if (kk >= 0) { pick = order[oi]; k = kk; }
    }
    if (pick < 0) return null;

    var n = keys.length;
    var slots = [{ i: pick, off: 0, card: cards[pick], img: fronts[k] }];
    if (this.three && n >= 3) {
      var sides = [];
      [-1, 1].forEach(function (off) {
        var g = ((k + off) % n + n) % n;
        for (var c = 0; c < cards.length; c++) {
          if (c !== pick && cardKey(cards[c]) === keys[g]) {
            sides.push({ i: c, off: off, card: cards[c], img: fronts[g] });
            return;
          }
        }
      });
      /* Both sides, or neither. A gallery arriving with one neighbour in
         place and the other appearing from nowhere is worse than one that
         opens out from its front piece, as it always has. */
      if (sides.length === 2 && sides[0].i !== sides[1].i) slots = slots.concat(sides);
    }
    return { k: k, key: keys[k], pick: pick, slots: slots };
  };

  /**
   * The gallery's pieces the rail lands on, watched from the start: each is
   * decoded the moment it arrives, so that when it is needed - a landing
   * card changing to it, or the gallery shown under the landed cards - it is
   * already pixels, not bytes.
   */
  Entrance.prototype.watchGallery = function (plan) {
    var self = this;
    /* Once every landing piece is in, the rest of the gallery may follow. */
    var settled = function () {
      if (plan.slots.every(function (s) { return s.ok || s.failed; }) && self.askLater) self.askLater();
    };
    /*
     * Decoded during the turn, not the build. The build draws every card
     * for the first time and needs the drawing threads to itself; the turn
     * only moves what is already drawn. A piece that arrives during the
     * build waits for it to end before it is turned into pixels.
     */
    var decode = function (sl) {
      if (sl.ok || sl.decoding) return;
      sl.decoding = true;
      var done = function () { sl.ok = true; settled(); };
      if (sl.img.decode) sl.img.decode().then(done, done);
      else done();
    };
    this.decodeGallery = function () {
      plan.slots.forEach(function (sl) { if (sl.loaded) decode(sl); });
    };
    plan.slots.forEach(function (sl) {
      var im = sl.img;
      var arrived = function () {
        sl.loaded = true;
        if (self.formed || !self.wheel.started) decode(sl);
      };
      if (im.complete && im.naturalWidth) { arrived(); return; }
      im.addEventListener('load', arrived, { once: true });
      im.addEventListener('error', function () { sl.failed = true; settled(); }, { once: true });
    });
  };

  /** The landing cards' photographs, laid out at their own shape (layoutPhoto). */
  Entrance.prototype.layoutLanding = function () {
    var plan = this.plan;
    if (!plan) return;
    for (var i = 0; i < plan.slots.length; i++) {
      var sl = plan.slots[i];
      var im = sl.card.querySelector('.lmat__face--front img');
      if (im) this.layoutPhoto(im, sl.img, sl.card);
    }
  };

  /**
   * THE LANDING CARDS ARE GIVEN THE GALLERY'S OWN PICTURES - UNSEEN.
   *
   * A card is 66px wide on a phone and is drawn from a 360px file; landing,
   * it grows to the gallery's 196px frame, where the gallery itself shows a
   * 720px file. So the photograph that came down was a smaller copy of the
   * gallery's picture, enlarged - and it sharpened visibly in the frame where
   * the gallery took over. Each landing card's photograph is changed for the
   * gallery's own file, decoded, but only at a moment its face is turned
   * away from the visitor: as the circles begin (the ring is built showing
   * its backs), or - if the file had not arrived by then - as the card
   * starts to turn over at the top. A picture changing under the eye is a
   * flash; a card that misses both moments simply lands with its own file.
   */
  Entrance.prototype.armSwaps = function () {
    var plan = this.plan, w = this.wheel;
    if (!plan || !CAN || !w.started || this.swapsArmed) return;
    this.swapsArmed = true;
    /* A plan made after the wheel began - the gallery had not been read yet
       (AHEAD OF THE PAGE) - books only the moments still ahead of it. */
    var gone = w.elapsed();
    plan.slots.forEach(function (sl) {
      var swap = function () {
        if (sl.swapped || !sl.ok) return;
        var im = sl.card.querySelector('.lmat__face--front img');
        var src = sl.img.currentSrc || sl.img.getAttribute('src');
        if (!im || !src) return;
        sl.swapped = true;
        im.removeAttribute('srcset');
        im.removeAttribute('sizes');
        im.src = src;
      };
      var times = w.faceAway(sl.i);
      for (var k = 0; k < times.length; k++) {
        var at = times[k] - gone;
        if (at > 0.02) w.timer(at, swap);
      }
    });
  };

  /**
   * The landed photographs dissolve only onto a gallery that is painted -
   * its pieces loaded and decoded - with a short ceiling, so a slow
   * connection lengthens the moment they lie there rather than baring an
   * empty frame under them.
   */
  Entrance.prototype.galleryReady = function (cb) {
    var self = this, plan = this.plan, fired = false;
    var pending = function () {
      return !!plan && plan.slots.some(function (s) { return !s.ok && !s.failed; });
    };
    var go = function () { if (fired) return; fired = true; cb(); };
    var look = function () {
      if (fired) return;
      if (!pending()) { go(); return; }
      self.at(80, look);
    };
    look();
    if (!fired) this.at(1600, go);
  };

  /**
   * A photograph laid out at its own shape, covering its card's face - the
   * same pixels `object-fit: cover` showed, but now with the whole picture
   * present behind the face's edges, so that a crop to the gallery's frame
   * can show what the gallery shows rather than only what the card did.
   */
  Entrance.prototype.layoutPhoto = function (im, ref, card) {
    var nw = im.naturalWidth || (ref && ref.naturalWidth);
    var nh = im.naturalHeight || (ref && ref.naturalHeight);
    var w = card.offsetWidth, h = card.offsetHeight;
    if (!nw || !nh || !w || !h) return;
    var a = nw / nh, W, H;
    if (a >= w / h) { H = h; W = h * a; } else { W = w; H = w / a; }
    var st = im.style;
    st.position = 'absolute';
    st.left = '50%';
    st.top = '50%';
    st.width = W + 'px';
    st.height = H + 'px';
    st.maxWidth = 'none';
    st.maxHeight = 'none';
    st.marginLeft = (-W / 2) + 'px';
    st.marginTop = (-H / 2) + 'px';
    im.__lmBox = { w: W, h: H, a: a };
  };

  /**
   * The ground does not lift onto an empty room.
   *
   * The wheel waits for its own photographs, but nothing waited for the one
   * behind it — so on a slow connection the bronze could clear onto a hero
   * with no picture in it. That is the exact failure the load window exists
   * to prevent, one layer further back.
   *
   * `room_cap` is short, because this is the last thing standing between the
   * visitor and the site: on any reasonable connection the hero photograph
   * is eager and high priority and arrived long ago, so this costs nothing
   * at all and only ever bites where it should.
   */
  Entrance.prototype.whenRoom = function (done) {
    var self = this;
    var img = document.getElementById('loomish-hero-bg-img');
    var fired = false;
    var go = function () { if (fired) return; fired = true; done(); };

    /* Not read yet (AHEAD OF THE PAGE): looked for again once the page has
       been, under the same ceiling. */
    if (!img && document.readyState === 'loading') {
      whenParsed(function () { if (!fired) self.whenRoom(go); });
      this.at((+this.root.dataset.roomCap || 2) * 1000, go);
      return;
    }
    if (!img || (img.complete && img.naturalWidth)) { go(); return; }
    img.addEventListener('load', go, { once: true });
    img.addEventListener('error', go, { once: true });
    this.at((+this.root.dataset.roomCap || 2) * 1000, go);
  };

  Entrance.prototype.clearTimers = function () {
    for (var i = 0; i < this.timers.length; i++) clock.clear(this.timers[i]);
    this.timers.length = 0;
  };

  /**
   * Act three. The bronze lifts and the site is simply there, behind a wheel
   * that is still turning.
   *
   * THE PAGE IS NOT GIVEN BACK HERE. It used to be, and that is what made a
   * hard scroll look stupid: the visitor was at the slider in half a second,
   * where its own scroll reveal rose to meet them while the rail was still
   * descending upstairs - two arrivals for one object. From here the door is
   * live instead: every gesture is caught, answered, and eventually spent.
   */
  Entrance.prototype.reveal = function () {
    var self = this;
    if (this.revealed) return;
    this.revealed = true;
    this.clearTimers();

    var root = this.root;
    var d = root.dataset;

    try { sessionStorage.setItem('lm-entered', '1'); } catch (e) {}

    root.classList.add('is-revealed');

    /*
     * The rest of the page is on. Dropped BEFORE the event is dispatched, so
     * a listener that checks the class is told the truth by both.
     */
    document.documentElement.classList.remove('lmat-veiled');

    /*
     * Say so out loud. The hero's wordmark and its line wait for this, and
     * so does the header's nav; without it all three played out behind an
     * opaque screen and the ground lifted onto a room with nothing left to
     * say. Anything else that should arrive with the site listens for the
     * same word.
     */
    say('loomish:revealed');

    if (!this.dissolve) return;

    /*
     * Reduced motion: nothing was held, and there is no rail to protect. Go
     * straight to the end of the outro, which is not an animation there but
     * it IS where the mark becomes the header's brand - and a visitor who
     * asked for no motion still gets a brand.
     */
    if (this.reduce) { this.outro(0); return; }

    if (this.gate) {
      /* The ground has begun to lift. Once it is gone the wash can be driven
         per frame, which it cannot be while a 0.9s transition is on it -
         every write would arrive nearly a second late. */
      this.at((+d.groundFade || 0.9) * 1000 + 60, function () { self.gate.loosen(); });
      this.gate.arm();

      /*
       * A ceiling on the hold, and only a ceiling. Nobody stares at a wheel
       * for this long, but a page that can never be scrolled again because
       * one gesture never arrived is not a risk worth carrying.
       */
      this.at((+d.gateCap || 14) * 1000, function () { self.gate.commit(); });

      /* With the circles, the door opens itself when they end. This is the
         ceiling under that, in case the turning tween is ever killed from
         outside: whatever happens, it goes in. */
      if (this.auto) {
        this.at((this.auto.t + 1.4) * 1000, function () { self.autoOpen(); });
      }
    } else {
      /* Not holding - the editor, or a hold-only threshold. Open it now. */
      this.release(0);
    }
  };

  /**
   * The circles are done: in it goes, exactly as if it had been pushed - the
   * Gate's commit lifts any bronze it had breathed back and starts the outro,
   * so there is one way in, whoever opens it.
   */
  Entrance.prototype.autoOpen = function () {
    if (this.done) return;
    if (this.gate) this.gate.commit();
    else this.outro(0);
  };

  /**
   * Act four. The photographs go down and become the slider; the mark goes up
   * and becomes the brand.
   *
   * One gesture splitting into its two destinations, in opposite directions
   * at the same moment. That opposition is the whole reason both halves stay
   * readable — send them the same way and it is just a screen clearing.
   *
   * `banked` is how hard the visitor pushed to get here. It is not discarded;
   * see glide().
   */
  Entrance.prototype.outro = function (banked) {
    var self = this;
    if (this.done) return;
    this.done = true;
    this.clearTimers();
    this.banked = banked || 0;

    var root = this.root;
    var d = root.dataset;
    var gsap = window.gsap;
    var mark = this.mark;
    var size = +d.brandSize;
    var m = this.mobile;

    /*
     * THE OUTRO ALWAYS REVEALS. Whatever opened the door - the push, a tap,
     * Tab, the ceiling - if reveal() has not run, its bookkeeping happens
     * here, in the frame the ground begins to clear: the session is marked,
     * the ground lifts (is-revealed), the veil class goes and the room is
     * told. Without this the hero's name and the header's icons waited on a
     * word that was never coming.
     */
    if (!this.revealed) {
      this.revealed = true;
      try { sessionStorage.setItem('lm-entered', '1'); } catch (e) {}
      root.classList.add('is-revealed');
      document.documentElement.classList.remove('lmat-veiled');
      say('loomish:revealed');
    }

    /* The gallery is about to be shown: all of it is asked for now, at the latest. */
    if (this.askLater) this.askLater();

    root.classList.add('is-leaving');

    // The mark stops belonging to the veil, so it survives the veil going
    var brand = null;
    var target = { left: window.innerWidth / 2 - size / 2, top: 16, size: size };
    var from = null;

    if (mark) {
      /*
       * Measured WITH the gate's offset still on it, then pinned to exactly
       * that box as the transform is cleared. The rectangle a transform
       * produces and the rectangle left/top/width/height produce are the
       * same rectangle, so there is nothing to see in the swap - and the
       * flight starts from where the visitor last had it rather than
       * snapping back to centre first.
       */
      from = mark.getBoundingClientRect();
      document.body.appendChild(mark);
      mark.classList.add('is-flying');
      mark.style.left = from.left + 'px';
      mark.style.top = from.top + 'px';
      mark.style.width = from.width + 'px';
      mark.style.height = from.height + 'px';
      if (gsap) gsap.set(mark, { x: 0, y: 0, scale: 1, rotation: 0 });

      brand = new Brand(mark, {
        brandSize: size,
        lift: +d.brandLift || 0
      });
      this.brand = brand;
      target = brand.home();
    }

    var line = +d.outroLine;

    /*
     * Reduced motion: no rail, no flight. The threshold simply stops being
     * there, and the page is handed over at once.
     */
    if (this.reduce) {
      this.handOff();
      root.style.display = 'none';
      /*
       * Straight to the hand-over. Anything short of land() once left a
       * 96px mark pinned to the middle of the viewport, fixed, for exactly
       * the visitors who asked for the least to happen on screen.
       */
      if (brand) brand.land();
      return;
    }

    /*
     * Where the photographs land (phones). The plan was made while the mark
     * was still on the bronze (landingPlan): the wheel card nearest its
     * middle that the gallery also holds comes down onto the gallery's front
     * frame, and - when the gallery's pieces either side of it are on the
     * wheel too - those two come down as its neighbours. No gallery, nothing
     * shared, or a gallery not on the screen: the descent, as on desktop.
     */
    var slots = null, landTo = null;
    var railEl = m ? document.querySelector('.loomish-slider-ad[data-lsa-rail]') : null;
    var rail = railEl && railEl.__lsaRail;
    var plan = this.plan;
    if (rail && plan && plan.slots.length) {
      slots = plan.slots;
      landTo = function () {
        if (!rail.showKey(plan.key)) return null;
        var r = rail.landRect();
        if (!r || r.width < 60 || r.top < 40 || r.bottom > window.innerHeight + 2) return null;
        return {
          cx: r.left + r.width / 2, cy: r.top + r.height / 2, w: r.width, h: r.height,
          d: rail.d,
          /* The gallery's own proportions, from the gallery (loomish-rail.js) */
          side: rail.step != null ? 1 - rail.step : 0.84,
          zoom: rail.zoom || 1.16,
          par: (rail.par != null ? rail.par : 11) / 100,
          radius: rail.radius ? rail.radius() : 3
        };
      };
    }

    /*
     * THE CARD STAYS UNTIL THE GALLERY IS UNDER IT (2026-10-15).
     *
     * The gallery's hold comes off two frames after the hand-over begins,
     * and this used to take the threshold away the moment the timeline
     * ended - so on a phone whose frames ran long through the landing (a
     * trace measured 300-440ms) the card was gone a frame or more before
     * the gallery was there: a blank band, then the gallery. Now, if the
     * hold is still on, leaving waits for the release, plus one painted
     * frame of the gallery under the card.
     *
     * Defined before the wheel is asked to move: with no wheel to move it
     * answers at once, inside the call.
     */
    var leaving = false;
    var leave = function (mode) {
      if (leaving) return;
      leaving = true;
      var gone = function () {
        var end = function () {
          root.style.display = 'none';
          self.wheel.destroy();
          /* The gallery opens out from the piece that has just landed on it -
             or, with three landed, is already open and simply begins. */
          say('loomish:landed');
        };
        /*
         * LANDED: THEY DISSOLVE INTO THE GALLERY (2026-10-21).
         *
         * Never taken away in a single frame. The front piece is the
         * gallery's own picture, at its size, crop and zoom; its neighbours
         * land at full strength while the gallery shows its neighbours a
         * little faded - so the cards are dissolved over the gallery now
         * lying under them, and the neighbours settle back to its strength
         * rather than jumping to it. On the compositor: whatever the page is
         * busy with at that moment, the dissolve is smooth.
         */
        if ((mode === 'three' || mode === 'one') && CAN && self.plan) {
          self.galleryReady(function () {
            /* The landed cards themselves, not the whole threshold: fading the
               root made the compositor redraw the full screen off-screen for
               every frame of the dissolve. Each card is the size of its frame,
               and faces front by now, so its fade costs nothing to the 3D. */
            var last = null;
            var slots = self.plan.slots;
            for (var si = 0; si < slots.length; si++) {
              last = self.wheel.keep(slots[si].card.animate([{ opacity: 1 }, { opacity: 0 }], {
                duration: 340, easing: 'cubic-bezier(0.33, 0, 0.2, 1)', fill: 'forwards'
              }));
            }
            if (last) last.onfinish = end;
            else end();
          });
        } else if (mode === 'none') {
          /* No wheel was built - the opening bowed out to a slow connection
             (TOO SLOW FOR THE PHOTOGRAPHS). The ground is lifting on its own
             transition; the threshold is taken away once it has, never in
             the middle of it. */
          self.at((+d.groundFade || 0.9) * 1000 + 80, end);
        } else {
          end();
        }
      };
      if (document.documentElement.classList.contains('lmat-forming')) self.afterRelease = gone;
      else gone();
    };

    this.wheel.outro({
      line: line,
      form: +d.outroForm,
      drop: +d.outroDrop,
      fadeAt: (+d.fadeAt || 52) / 100,
      gap: m ? +d.railGapM : +d.railGap,
      scale: (m ? +d.railScaleM : +d.railScale) / 100,
      edge: +d.railEdge / 100,
      tilt: +d.railTilt,
      depth: +d.railDepth,
      lay: +d.railLay,
      fall: +d.fallDist / 100,
      stream: +d.outroStream / 100,
      slots: slots,
      land: landTo
    }, function (mode) {
      /* Three landed: the gallery is opened out under them before it is
         shown, so it appears exactly as the photographs lying on it. */
      if (mode === 'three' && rail) { try { rail.spread(true); } catch (e) {} }
      self.handOff();
      /* Landed: the dissolve begins as soon as the gallery is painted under
         the cards - while they are still finishing their last pixel. */
      if (mode === 'three' || mode === 'one') leave(mode);
    }, function (mode) {
      /* Belt and braces: the slider is never left held because a timeline
         callback did not fire. A held slider is an invisible section. */
      self.handOff();
      leave(mode);
    });

    /* And a ceiling over the outro itself. The page comes back even if the
       timeline is killed by something outside this file. */
    this.at((+d.outroLine + +d.outroForm + +d.outroDrop) * 1000 + 1400,
            function () { self.handOff(); });

    /*
     * The mark holds at the centre while the ring straightens — it is what
     * the photographs are standing around, so it does not leave before they
     * have finished being a ring. It lifts the instant the rail begins to
     * open, and is in the header before the rail has landed: the brand
     * arrives at the top as the images arrive at the bottom.
     *
     * TRANSFORM, NOT LAYOUT, and on the compositor (2026-10-21) like the
     * wheel, so the flight is as smooth as the ring whatever the page is
     * doing. It aims at the header's own mark, centre to centre and size to
     * size, and on arrival the two are the same pixels.
     */
    if (mark && from) {
      var fromCX = from.left + from.width / 2;
      var fromCY = from.top + from.height / 2;
      var dx = (target.left + target.size / 2) - fromCX;
      var dy = (target.top + target.size / 2) - fromCY;
      var sc = from.width ? target.size / from.width : 1;
      /* Home: the bar's mark takes over in this same instant. */
      var home = function () {
        brand.land();
        if (gsap) gsap.set(mark, { x: 0, y: 0, scale: 1 });
      };

      /* Kept, because it is the one movement that is NOT the wheel's - so
         wheel.destroy() cannot stop it, and settle() has to be able to end a
         flight caught in mid-air by a restored page. */
      if (CAN) {
        var fl = this.flight = mark.animate([
          { transform: 'translate3d(0px, 0px, 0px) scale(1)' },
          { transform: 'translate3d(' + dx.toFixed(2) + 'px, ' + dy.toFixed(2) + 'px, 0px) scale(' + sc.toFixed(4) + ')' }
        ], {
          duration: +d.flight * 1000,
          delay: line * FORM_AT * 1000,
          /* GSAP's power3.inOut, as the compositor writes it */
          easing: 'cubic-bezier(0.76, 0, 0.24, 1)',
          fill: 'both'
        });
        fl.onfinish = function () {
          home();
          try { fl.cancel(); } catch (e) {}
        };
      } else if (gsap) {
        this.flight = gsap.to(mark, {
          x: dx, y: dy, scale: sc,
          duration: +d.flight, delay: line * FORM_AT, ease: 'power3.inOut',
          onComplete: home
        });
      } else {
        brand.land();
      }
    } else if (brand) {
      brand.land();
    }
  };

  /** Nothing of the entrance advances while nobody is looking. */
  Entrance.prototype.hold = function (hidden) {
    this.wheel.hold(hidden);
    if (this.flight) holdAnim(this.flight, hidden);
  };

  /**
   * Give the page to the slider, and give it back to the visitor.
   *
   * Until this moment the slider is held down and the page cannot move. The
   * two belong together: the slider must not be sitting there fully formed
   * while the photographs are still on their way to it, and the visitor must
   * not be able to get down there and watch that happen.
   *
   * The transition class goes on a frame BEFORE the hold comes off, because a
   * transition is read from the state being moved TO. Declare it and drop the
   * hold in the same frame and the slider does not ease in, it simply appears.
   */
  Entrance.prototype.handOff = function () {
    if (this.handed) return;
    this.handed = true;

    var self = this;
    var html = document.documentElement;
    html.classList.add('lmat-handoff');

    var release = function () {
      html.classList.remove('lmat-forming');
      self.release(self.banked);
      /* The landed card, waiting for this: it goes once the gallery under it
         has been painted. */
      if (self.afterRelease) {
        var gone = self.afterRelease;
        self.afterRelease = null;
        if (window.requestAnimationFrame) window.requestAnimationFrame(function () { window.requestAnimationFrame(gone); });
        else gone();
      }
    };
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () { window.requestAnimationFrame(release); });
    } else {
      release();
    }
  };

  /**
   * The page comes back.
   *
   * Everything the gate was holding is let go of in one place, and it is
   * called from every path that can end the threshold - the handover, the
   * ceilings, and destroy() - because the one unforgivable outcome here is a
   * page that cannot be scrolled.
   */
  Entrance.prototype.release = function (banked) {
    if (this.freed) return;
    this.freed = true;

    var self = this;
    var html = document.documentElement;

    if (this.gate) this.gate.unbind();
    html.classList.remove('lmat-locked');
    holdTriggers(false);

    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = priorRestore || 'auto';
      }
    } catch (e) {}

    if (!window.requestAnimationFrame) return;

    /* The lock has to be off the layout before anything can read it */
    window.requestAnimationFrame(function () {
      /*
       * ANYTHING THAT MEASURED THE SCROLLER WHILE THE PAGE WAS HELD
       * MEASURED A PAGE WITH NO HEIGHT.
       *
       * overflow:hidden makes the document unscrollable, and ScrollTrigger
       * caches start and end positions when a trigger is created. The hero's
       * wordmark fade is created at the reveal - deliberately, so the name
       * arrives with the room - which is squarely inside the window this
       * file holds shut. Handing the page back is therefore also the moment
       * to tell it to look again.
       */
      try { if (window.ScrollTrigger) window.ScrollTrigger.refresh(); } catch (e) {}
      if (banked && !self.reduce) self.glide(banked);
    });
  };

  /**
   * What they pushed with is what they travel on.
   *
   * They leaned on the door for the better part of a second and then watched
   * a rail fall for two more. Opening onto a page that has not moved an inch
   * would make all of that effort read as having been thrown away. So the
   * push is spent: a short, decelerating glide down, capped well short of a
   * screen so the hero is never skipped, and abandoned the instant they take
   * the wheel back. Resistance, release, and then the site.
   */
  Entrance.prototype.glide = function (banked) {
    var px = Math.min(banked * 0.5, window.innerHeight * this.carry);
    if (!(px > 24)) return;

    var start = window.pageYOffset || document.documentElement.scrollTop || 0;
    var t0 = 0, dur = 720, stopped = false;

    var off = function () {
      window.removeEventListener('wheel', abort);
      window.removeEventListener('touchstart', abort);
      window.removeEventListener('keydown', abort);
    };
    var abort = function () { stopped = true; off(); };

    window.addEventListener('wheel', abort, { passive: true });
    window.addEventListener('touchstart', abort, { passive: true });
    window.addEventListener('keydown', abort);

    var step = function (now) {
      if (stopped) return;
      if (!t0) t0 = now;
      var t = Math.min(1, (now - t0) / dur);
      var e = 1 - Math.pow(1 - t, 3);
      window.scrollTo(0, start + px * e);
      if (t < 1) window.requestAnimationFrame(step);
      else off();
    };
    window.requestAnimationFrame(step);
  };

  /**
   * Put the mark in the header without flying it there.
   *
   * The flight is the good version of this; it needs a start rectangle, a
   * timeline and a page that is standing still. Where none of that applies —
   * a page restored from the back-forward cache, mid-entrance — the mark
   * still has to end up being the brand, or the header sits empty over the
   * hero, because `.lh-logo` is transparent until the bar goes solid.
   */
  Entrance.prototype.installBrand = function () {
    if (!this.mark) {
      document.documentElement.classList.remove('lmat-brand-held');
      return;
    }
    if (!this.brand) {
      var d = this.root.dataset;
      this.brand = new Brand(this.mark, { brandSize: +d.brandSize, lift: +d.brandLift || 0 });
    }
    this.brand.land();
  };

  /**
   * End it now, with no theatre at all.
   *
   * For a page that has come back rather than loaded: the visitor has been
   * through the site and returned, so what they need is a working page, not
   * the door a second time. Everything the four acts would eventually have
   * done is done here in one step, and every one of those steps is idempotent
   * so this is safe to call on a threshold that already finished.
   */
  Entrance.prototype.finish = function () {
    var html = document.documentElement;

    /*
     * The test is whether the page has actually been GIVEN BACK, not whether
     * the outro has begun. `done` only means the last act started — so
     * guarding on it meant a page restored midway through the rail's descent
     * skipped every line below and stayed locked, with the slider still held,
     * for good. That is the precise failure this whole file exists to make
     * impossible, so the condition has to be the end state itself.
     */
    if (!this.handed || !this.freed) {
      this.done = true;
      this.revealed = true;
      this.clearTimers();
      if (this.gate) { this.gate.open = true; this.gate.unbind(); }
      try { sessionStorage.setItem('lm-entered', '1'); } catch (e) {}

      this.wheel.destroy();
      this.installBrand();

      this.root.classList.add('is-revealed');
      this.root.style.display = 'none';

      /* The room must never be left waiting on a word that is not coming. */
      html.classList.remove('lmat-veiled');
      say('loomish:revealed');

      /* No frame delay: the transition exists so the slider can be seen to
         arrive, and there is nobody watching it arrive on this path. */
      this.handed = true;
      html.classList.add('lmat-handoff');
      html.classList.remove('lmat-forming');
      this.release(0);
    }

    this.settle();
  };

  /**
   * A restored page can be a different page: another viewport, another
   * scroll position, a layout measured before any of it was true.
   */
  Entrance.prototype.settle = function () {
    /* A flight caught in mid-air would go on tweening the transform while
       Brand writes left and top, and the two would fight every frame. */
    if (this.flight) {
      try {
        if (this.flight.cancel) this.flight.cancel();
        else if (this.flight.kill) this.flight.kill();
      } catch (e) {}
      this.flight = null;
    }
    if (this.mark && window.gsap) window.gsap.set(this.mark, { x: 0, y: 0, scale: 1 });

    /* A flight caught in mid-air has nowhere to be but home. */
    if (this.brand) this.brand.land();
    try { if (window.ScrollTrigger) window.ScrollTrigger.refresh(); } catch (e) {}
  };

  Entrance.prototype.destroy = function () {
    this.clearTimers();
    if (this.gate) this.gate.unbind();
    this.wheel.destroy();
    if (this.brand) this.brand.destroy();
    document.documentElement.classList.remove('lmat-brand-held');
    /* Whatever took this apart, the page must be left usable — and the room
       must be let out of its wait, or the hero never speaks at all. */
    document.documentElement.classList.remove('lmat-forming');
    document.documentElement.classList.remove('lmat-locked');
    document.documentElement.classList.remove('lmat-veiled');
    holdTriggers(false);
    this.freed = true;
  };

  /* ------------------------------------------------------------------ boot */

  function sweep(scope) {
    for (var i = live.length - 1; i >= 0; i--) {
      var t = live[i];
      var gone = !document.body.contains(t.root);
      var inScope = scope && scope.contains && scope.contains(t.root);
      if (!gone && !inScope) continue;
      try { t.destroy(); } catch (e) {}
      t.root.__lmat = null;
      t.root.removeAttribute('data-mounted');
      live.splice(i, 1);
    }
  }

  function mount(el) {
    /* '1' is mounted; 'gave-up' is the section's failsafe having already
       handed the page over - too late to begin a threshold now. */
    if (el.dataset.mounted) return;
    el.dataset.mounted = '1';

    var d = el.dataset;
    var html = document.documentElement;
    var editor = d.editor === '1';
    var dissolve = d.after === 'dissolve';

    var seen = false;
    var forced = false;
    try {
      forced = /[?&#]enter\b/.test(location.search + location.hash);
      seen = !forced && sessionStorage.getItem('lm-entered') === '1';
    } catch (e) {}

    if (!forced && d.once === '1' && seen && dissolve && !editor) {
      el.style.display = 'none';
      html.classList.remove('lmat-brand-held');
      return;
    }

    /*
     * A visitor who has asked for no motion is not held for one. There is no
     * rail for a hold to protect, and holding a page still is itself a
     * motion decision made on somebody else's behalf.
     */
    var reduce = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    html.classList.remove('lmat-seen');
    el.style.display = '';
    if (dissolve && !editor && !reduce) {
      /*
       * The threshold is the top of the page, so start there, and make sure
       * the browser does not put it back. A restored scroll position would
       * otherwise leave the visitor half way down the site behind the veil,
       * with the outro sending photographs down toward a slider that is
       * already above them.
       */
      try {
        if ('scrollRestoration' in window.history) {
          if (priorRestore === null) priorRestore = window.history.scrollRestoration;
          window.history.scrollRestoration = 'manual';
        }
      } catch (e) {}
      try { window.scrollTo(0, 0); } catch (e) {}

      html.classList.add('lmat-locked');
      /* The bar's own mark waits for this one to fly up to it. */
      html.classList.add('lmat-brand-held');
      /*
       * Hold the slider down from the very first frame - a second lock, on
       * the section rather than on the page, so that neither a failure here
       * nor an author linking straight to it can put a fully formed slider
       * in front of photographs that are still on their way to it.
       *
       * Never in the Theme Editor: an author editing the slider would find
       * it invisible and reasonably conclude it was broken.
       */
      html.classList.add('lmat-forming');
      holdTriggers(true);
      /* ScrollTrigger is deferred, so a threshold that starts ahead of the
         page finds it missing - the hold goes on as soon as it is there. */
      if (!window.ScrollTrigger && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          if (html.classList.contains('lmat-locked')) holdTriggers(true);
        });
      }
    }

    try {
      new Entrance(el);
    } catch (e) {
      // A threshold that fails must never become a locked door — nor a
      // silent hero waiting on a reveal that is not coming.
      holdTriggers(false);
      html.classList.remove('lmat-locked');
      html.classList.remove('lmat-forming');
      html.classList.remove('lmat-veiled');
      html.classList.remove('lmat-brand-held');
      el.style.display = 'none';
      if (window.console) console.error('[loomish-atmosphere]', e);
    }
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-lmat]:not([data-mounted])');
    for (var i = 0; i < nodes.length; i++) mount(nodes[i]);
  }

  /*
   * The one thing the visible clock cannot reach: a CSS animation.
   *
   * Those are not driven by requestAnimationFrame, so a hidden tab does not
   * reliably stop them — which would leave the mark's arrival finished behind
   * a threshold that has otherwise held its place, and the visitor coming
   * back to a beat that has already been and gone. Pausing makes the rule
   * true without exception: nothing advances while nobody is looking.
   */
  document.addEventListener('visibilitychange', function () {
    document.documentElement.classList.toggle('lmat-away', !!document.hidden);
    /* And the compositor's animations, which a hidden tab does not stop
       either: the ring is held exactly where it was, like everything else. */
    for (var i = 0; i < live.length; i++) {
      try { live[i].hold(!!document.hidden); } catch (e) {}
    }
  });

  /*
   * STARTED THE MOMENT IT ARRIVES (2026-10-25).
   *
   * This file was deferred: it ran only once the whole page had been read,
   * and after every deferred script ahead of it. A visit from Instagram is
   * always a cold one - the app's browser shares no cache with the phone's -
   * and on a mid phone over 4G that measured eight seconds before it began:
   * the mark alone on the bronze the whole time, and as often as not the
   * section's failsafe cutting to a half-painted page before the opening
   * could start at all.
   *
   * It is async now and asked for first (theme.liquid preloads it), so it
   * starts as soon as it is here. The threshold's own markup sits above this
   * script in its section, so it is always there to mount; what lies further
   * down - the room, the gallery - is waited for where it is needed (AHEAD
   * OF THE PAGE).
   */
  if (document.readyState !== 'loading' || document.querySelector('[data-lmat]:not([data-mounted])')) boot();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', function (e) { sweep(e.target); boot(); });
  document.addEventListener('shopify:section:unload', function (e) { sweep(e.target); });

  /* ------------------------------------------------- coming back to it ---- */

  /**
   * Did this page come BACK, rather than load?
   *
   * `persisted` is the back-forward cache saying so outright. The navigation
   * type covers the other half: a back or forward that missed the cache and
   * really did reload. Both mean the same thing to us — the visitor has been
   * somewhere else and returned — and both should behave identically, or
   * "press back" would give one experience on Safari and another on Chrome.
   */
  function cameBack(e) {
    if (e && e.persisted) return true;
    try {
      var p = window.performance;
      if (p && p.getEntriesByType) {
        var nav = p.getEntriesByType('navigation')[0];
        if (nav && nav.type === 'back_forward') return true;
      }
      /* The old API, for engines without the entry type. */
      if (p && p.navigation && p.navigation.type === 2) return true;
    } catch (err) {}
    return false;
  }

  /**
   * A page restored from the back-forward cache DOES NOT LOAD AGAIN. No
   * DOMContentLoaded, no load — nothing in this file re-runs. It comes back
   * exactly as it was left, so a visitor who left DURING the entrance returns
   * to a page that is still locked, still veiled, with the slider still held
   * out of sight and a wheel frozen mid-build. Whether the pending work
   * resumes at all is up to the engine, and where it does not, that page
   * never unlocks again.
   *
   * So an unfinished threshold is finished on the spot, and a finished one is
   * re-settled against whatever the viewport is now.
   */
  window.addEventListener('pageshow', function (e) {
    if (!cameBack(e)) return;
    for (var i = live.length - 1; i >= 0; i--) {
      try { live[i].finish(); } catch (err) {
        if (window.console) window.console.error('[loomish-atmosphere]', err);
      }
    }
  });

  /**
   * `scrollRestoration` belongs to the session history, not to this page.
   * Set it to manual and navigate away mid-entrance and every back and
   * forward in that tab stops restoring where the visitor was — for the rest
   * of the session, on every page of the site. It goes back whatever happens
   * to this one.
   */
  window.addEventListener('pagehide', function () {
    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = priorRestore || 'auto';
      }
    } catch (e) {}
  });
})();
